import {getCameraMercatorZ, patchDeckTransform} from './helpers/maplibre-compat';
import {featureEach} from '@turf/meta';
import {Evented, FullscreenControl, LngLat, Map as Mapbox, Marker, MercatorCoordinate, NavigationControl} from 'maplibre-gl';
import AnimatedPopup from 'mapbox-gl-animated-popup';
import animation from './animation';
import Clock from './clock';
import configs from './configs';
import {ClockControl, SearchControl} from './controls';
import Dataset from './dataset';
import {Airport, Bus, Flight, FlightStatus, GTFSRoute, GTFSStop, GTFSTrip, Operator, POI, RailDirection, Railway, Station, Train, TrainTimetables, TrainType, TrainVehicleType} from './data-classes';
import extend from './extend';
import * as helpers from './helpers/helpers';
import {wrapAngle} from './helpers/wrap-angle';
import {pickObject, resetCursor} from './helpers/helpers-deck';
import * as helpersGeojson from './helpers/helpers-geojson';
import * as helpersMapbox from './helpers/helpers-mapbox';
import {GeoJsonLayer, ThreeLayer, Tile3DLayer, TrafficLayer} from './layers';
import {blendMs, planSection, positionAt, sectionFor, staleTrips} from './helpers/trajectory';
import {isExpired, loadBusData, loadDictionary, loadDynamicBusData, loadDynamicFlightData, loadDynamicTrainData, loadStaticData, loadTimetableData, updateOdptUrl} from './loader';
import {encodeRoofNumber} from './helpers/roof-number.js';
import {AboutPanel, BusPanel, BusStopPanel, RailwayPanel, SharePanel, StationPanel, TrackingModePanel, TrainPanel} from './panels';
import nearestCloserPointOnLine from './turf/nearest-closer-point-on-line';

const AIRLINES_FOR_ANA_CODE_SHARE = ['ADO', 'SFJ', 'SNJ'];

const DEGREE_TO_RADIAN = Math.PI / 180;

// 카메라 각도를 열차 진행방향에서 파생하는 트래킹 모드. 사용자가 돌리면 절대각이 아니라
// 진행방향 기준 상대각으로 기억해야 열차가 커브를 돌 때 추격 카메라가 살아남는다.
// 나머지 모드(helicopter/drone/bird)는 자동 공전이므로 사용자가 잡으면 공전을 포기한다.
// 'heading' 은 topback 의 구 이름이다 (tracking-mode-panel 이 아직 매핑한다).
const HEADING_TRACKING_MODES = new Set(['back', 'front', 'topback', 'topfront', 'heading']);

// 혼잡도 밴드. 공식 해석을 그대로 따른다 — 34% 는 좌석이 다 찬 상태, 100% 는 량당
// 160명이라는 공표 정원이고 그 위는 어깨가 닿는다.
const CONGESTION_BANDS = [
    {below: 34, color: '#4CAF50'},
    {below: 80, color: '#FFC107'},
    {below: 130, color: '#FF7043'},
    {below: Infinity, color: '#E53935'}
];

// 혼잡도 오버레이의 불투명도, 색 텍스처의 네 장([color0..3]) 각각에 대해.
//
// **버스와 같이 지붕 삼각형에만 싣는다.** 노선색 띠(color2)를 덮던 구판은 편성
// 전체가 혼잡도색이 돼 어느 노선인지가 흐려졌다 - 버스에서 차체에 점유도를 섞었다가
// 되돌린 것과 같은 문제다.
//
// 삼각형은 지붕에 둘이지만(tools/make-car-texture.py) **한 열차에 동시에 보이는 건
// 하나가 기본이다.** 뒤 삼각형(color0)의 기본값이 지붕색이라 안 보이기 때문이다.
//   일반 열차 - 앞 삼각형(color3) 하나를 혼잡도색으로 바꾼다. 지붕에 삼각형은 계속 하나다.
//   급행 열차 - 앞 삼각형은 이미 빨강으로 급행을 말하고 있으므로 뒤 삼각형(color0)에
//               혼잡도를 실어 둘이 된다. 급행 표식을 지우지 않는 유일한 자리다.
// color1 은 그대로 남는다 - 량별 혼잡도 같은 다음 축이 쓸 자리다.
const CONGESTION_TINT_ALPHA = [0, 0, 0, 1];
const CONGESTION_TINT_ALPHA_EXPRESS = [1, 0, 0, 0];

// 지붕 색. 뒤 삼각형의 기본값이 이 값이라 급행이 아닌 열차에서는 지붕에 묻힌다.
// tools/make-car-texture.py 의 WHITE 와 같은 값이어야 한다.
const CAR_ROOF_COLOR = '#fafafa';

// 버스 점유도. GTFS-RT occupancy_status 는 **차량이 직접 주는 실시간 값**이라 열차의
// 혼잡도(30분 역·방향 평균)와 성질이 다르다. 그래서 열차는 선택한 편성만 덮지만
// 버스는 전 차량이 항상 자기 값을 들고 있다 - 노이즈가 아니라 그 버스의 지금 상태다.
// 색은 CONGESTION_BANDS 와 같은 4단계다 (지하철과 눈이 같은 뜻으로 읽어야 한다).
// 인덱스 = occupancy_status 값: 1 MANY_SEATS=여유 · 2 FEW_SEATS=보통 ·
// 3 STANDING_ROOM_ONLY=혼잡 · 4 CRUSHED=매우혼잡.
//
// **차체가 아니라 지붕 삼각형(시안 억센트, color3)에 싣는다.** 차체(마젠타, color0)는
// 노선 유형색을 그대로 유지해야 한다 - 차체에 섞었을 때 01A 순환(주황)과 '보통'
// 혼잡도 간선 버스가 같은 색이 돼 노선을 구분할 수 없었다. 레퍼런스 열차가
// 차체=노선색 / 억센트=급행표식으로 가른 것과 같은 구조다.
//
// ⚠️ **0 은 비워 둔다.** protobuf 에서 필드 부재와 값 0(EMPTY)은 클라이언트에서
// 구분되지 않는다 (protobufjs 가 없는 필드를 0 으로 읽는다). 0 에 색을 주면
// 점유도를 안 주는 피드의 버스가 전부 '여유'로 칠해진다 - '데이터 없음' 과
// '한산함' 은 다른 정보다. 등록하지 않으면 getBusColorIndex 가 노선색 항목으로
// 떨어지고, 그 항목의 억센트는 아래 흰색이라 삼각형이 방향 표식으로만 남는다.
const BUS_OCCUPANCY_COLOR = [undefined, '#4CAF50', '#FFC107', '#FF7043', '#E53935'];

// 점유도를 모를 때의 지붕 삼각형 색. 차체가 어느 노선색이든 갈리는 밝은 무채색이다.
const BUS_ACCENT_UNKNOWN = '#fafafa';

// Replace NavigationControl._updateZoomButtons to support disabling the control
const _updateZoomButtons = NavigationControl.prototype._updateZoomButtons;
NavigationControl.prototype._updateZoomButtons = function() {
    const me = this,
        {_disabled, _zoomInButton, _zoomOutButton, _compass} = me;

    _updateZoomButtons.apply(me);

    if (_disabled) {
        _zoomInButton.disabled = _zoomOutButton.disabled = true;
        _zoomInButton.setAttribute('aria-disabled', 'true');
        _zoomOutButton.setAttribute('aria-disabled', 'true');
    }
    _compass.disabled = !!_disabled;
    _compass.setAttribute('aria-disabled', (!!_disabled).toString());
};

NavigationControl.prototype.enable = function() {
    const me = this;

    delete me._disabled;
    me._updateZoomButtons();
};

NavigationControl.prototype.disable = function() {
    const me = this;

    me._disabled = true;
    me._updateZoomButtons();
};

export default class extends Evented {

    constructor(options) {
        super();

        const me = this;

        options = extend({
            hash: false,
            // MapLibre 기본 maxPitch 는 60 인데 Mapbox v3 는 85 다. 기본 pitch 가
            // 60 이라 그대로 두면 **더 기울일 수가 없어** 조작감이 달라진다.
            maxPitch: 85,
            // 우클릭 드래그 회전을 Mapbox 와 같은 **선형** 모델로 바꾼다.
            // MapLibre 기본값(orbital)은 커서 위치에 따라 세 갈래로 갈린다 -
            // 중심에서 위아래로 100px 밖이면 중심 기준 각도(턴테이블), 안이면
            // dx x 0.8, 그런데 중심보다 **위쪽이면 부호를 뒤집는다**. 그래서
            // 같은 방향으로 끌어도 위/아래에서 반대로 돈다.
            // (`node_modules/maplibre-gl/src/ui/handler/mouse.ts`
            //  generateMouseRotationHandler)
            // Mapbox 는 커서 위치와 무관하게 bearing = dx x 0.8 뿐이다.
            // pitch(dy x -0.5)는 양쪽이 이미 같다.
            aroundCenter: false,
            center: configs.defaultCenter,
            zoom: configs.defaultZoom,
            bearing: configs.defaultBearing,
            pitch: configs.defaultPitch,
            dataUrl: configs.dataUrl,
            dataSources: configs.dataSources,
            clockControl: true,
            searchControl: true,
            navigationControl: true,
            fullscreenControl: true,
            modeControl: true,
            configControl: true,
            trackingMode: configs.defaultTrackingMode,
            ecoMode: configs.defaultEcoMode,
            ecoFrameRate: configs.defaultEcoFrameRate
        }, options);

        me.lang = helpers.getLang(options.lang);
        me.dataUrl = options.dataUrl;
        me.dataSources = options.dataSources.map(source => normalizeDataSource(source, options.secrets));
        me.container = typeof options.container === 'string' ?
            document.getElementById(options.container) : options.container;
        me.secrets = options.secrets;
        me.modelOrigin = MercatorCoordinate.fromLngLat(options.center);
        me.exitPopups = [];

        me.clockControl = options.clockControl;
        me.searchControl = options.searchControl;
        me.navigationControl = options.navigationControl;
        me.fullscreenControl = options.fullscreenControl;
        me.modeControl = options.modeControl;
        me.configControl = options.configControl;
        me.clock = new Clock();

        me.hiddenRailways = new Set();
        // 혼잡도는 기본으로 켜둔다. 포커스한 열차 하나만 덮으므로 평소 화면을 어지르지
        // 않고, 켜는 걸 사용자가 발견해야 쓸 수 있는 기능이 되지 않는다.
        me.congestionMode = true;
        me.cinemaDuration = configs.cinemaDuration;
        me.searchMode = 'none';
        me.viewMode = configs.defaultViewMode;
        me.trackingMode = options.trackingMode;
        me.trackingParams = {
            zoom: {},
            bearing: {},
            pitch: {}
        };
        me.lastCameraParams = {};
        me.initialSelection = options.selection;
        me.clockMode = configs.defaultClockMode;
        me.isEditingTime = false;
        me.ecoMode = options.ecoMode;
        me.ecoFrameRate = options.ecoFrameRate;

        me.lastDynamicUpdate = {};
        me.lastRepaint = 0;
        me.frameRateFactor = 1;

        // The inner map container overrides the option
        options.container = initContainer(me.container);

        // This style overrides the option
        options.style = helpers.resolveAssetUrl('assets/style.json');

        // The custom attribution will be appended only if ConfigControl is visible
        if (!options.configControl) {
            options.customAttribution = helpers.flat([options.customAttribution, configs.customAttribution]);
        }

        // 저작권 표시는 접어 둔다 - 오른쪽 아래 ⓘ 만 보이고 누르면 펼쳐진다.
        // 문구가 길어(MapLibre · OpenFreeMap · OpenMapTiles · OpenStreetMap ·
        // 서울교통공사 …) 모바일 375px 에서 두 줄을 먹고 지도를 가렸다.
        //
        // ⚠️ **표시 의무는 그대로 지킨다.** 지우는 게 아니라 접는 것이고, ⓘ 로
        // 항상 도달할 수 있다 - MapLibre 가 제공하는 `compact` 가 OSM/ODbL 표시를
        // 만족하는 표준 방식이다. 호출부가 명시적으로 준 값이 있으면 그걸 존중한다.
        if (options.attributionControl === undefined) {
            options.attributionControl = {compact: true};
        }

        me.map = new Mapbox(options);
        patchDeckTransform(me.map);

        // ⚠️ **`compact: true` 만으로는 접히지 않는다.** MapLibre 5 의
        // `_updateCompact` 는 컨테이너에 `maplibregl-compact` 가 **없을 때**
        // `compact` 와 `compact-show` 를 **같이** 붙인다. 그래서 기본이 펼침이다
        // (2026-09-10 실측: 폭 385px · 본문 50자).
        //
        // ⚠️ **생성 직후에 떼면 안 된다.** 그 시점엔 아직 클래스가 없어서 제거가
        // 무효고, 그 뒤 MapLibre 가 둘 다 붙인다 (한 번 그렇게 배포했다).
        // 붙은 뒤에 뗀다. `compact` 는 남기므로 위 분기가 다시 안 탄다.
        //
        // 사용자가 ⓘ 를 누르면 MapLibre 가 다시 붙여 펼치므로 **초기 상태만**
        // 바꾸는 것이고 저작권 표시 의무는 그대로다 (접힌 상태 24x24px).
        if (options.attributionControl && options.attributionControl.compact) {
            const collapse = () => {
                const attrib = me.map.getContainer().querySelector('.maplibregl-ctrl-attrib');

                if (attrib && attrib.classList.contains('maplibregl-compact')) {
                    attrib.classList.remove('maplibregl-compact-show');
                    attrib.removeAttribute('open');
                    return true;
                }
                return false;
            };

            if (!collapse()) {
                me.map.once('load', collapse);
                // load 전에 붙는 경우도 있어 한 틱 뒤에도 한 번 본다
                setTimeout(collapse, 0);
            }
        }

        // Mapbox 타일셋은 저작권 문자열을 조금씩 다르게 준다 — 3D 건물·교량 타일셋
        // 판은 URL 끝 슬래시와 피드백 링크가 다르다. AttributionControl 은 한쪽이
        // 다른 쪽의 부분 문자열일 때만 합치므로 그 차이로 같은 말이 ' | ' 로 두세 번
        // 이어 붙는다 (줌 16 이상에서 3D 소스가 로드되면 재현된다). Mapbox 소스의
        // 저작권을 가장 완전한 한 판으로 맞춰 컨트롤이 스스로 합치게 한다. 문자열을
        // 우리가 적지 않는 이유는 링크가 바뀌어도 따라가야 하기 때문이다.
        const mapboxSources = new Set();
        let fullestAttribution = '';

        me.map.on('sourcedata', ({sourceId, sourceDataType}) => {
            if (sourceDataType !== 'metadata') {
                return;
            }
            const source = me.map.getSource(sourceId),
                attribution = (source || {}).attribution || '';

            if (!attribution.includes('mapbox.com/about/maps')) {
                return;
            }
            mapboxSources.add(sourceId);
            if (attribution.length > fullestAttribution.length) {
                fullestAttribution = attribution;
            }
            for (const id of mapboxSources) {
                me.map.getSource(id).attribution = fullestAttribution;
            }
        });

        for (const event of configs.events) {
            me.map.on(event, me.fire.bind(me));
        }

        me.map.once('idle', () => {
            helpers.measureFrameRate().then(frameRate => {
                me.frameRateFactor = Math.min(60 / frameRate, 2);
            });
        });

        // The clock runs in the default time zone (configs.defaultTimezone) unless
        // one is determined from GTFS data (see refreshBusData). me.timezone holds
        // the established zone once known: the default when the map loads the fixed
        // mini-tokyo-3d data, or the first GTFS feed's agency_timezone when no such
        // data is loaded.
        me.timezone = me.dataUrl ? me.clock.getTimezone() : undefined;

        me.gtfs = new Map();
        // IDs of GTFS sources whose data is currently being loaded. Tracked
        // separately from me.gtfs (which is only populated once loading finishes)
        // so overlapping refreshBusData calls don't load the same source twice.
        me.gtfsLoading = new Set();

        Promise.all([
            loadDictionary(me.lang)
                .then(dict => (me.dict = dict))
                .catch(error => {
                    showErrorMessage(me.container);
                    throw error;
                }),
            loadStaticData(me.dataUrl, me.clock)
                .then(me.initData.bind(me))
                .catch(error => {
                    showErrorMessage(me.container);
                    throw error;
                }),
            new Promise(resolve => {
                me.map.once('styledata', () => {
                    // Defer by a microtask: 'styledata' fires in the middle of a style
                    // update, and setting the lights synchronously here would be wiped
                    // by the rest of that update. Running after it lets the lights
                    // stick, so they are correct from the first frame and still present
                    // when initialize() reads them via getLight().
                    Promise.resolve().then(() => {
                        const now = me.clock.getTime();

                        helpersMapbox.setSunlight(me.map, now, me.viewMode === 'underground' ? 0 : 1);
                        me.lastSunlightCenter = me.map.getCenter();
                        me.lastSunlightTime = now;
                        me.lastSunlightRefresh = Date.now();
                        resolve();
                    });
                });
            })
        ]).then(me.initialize.bind(me));

        me.refreshBusData();
    }

    /**
     * Returns the Mapbox's Map object used in the map.
     * @returns {mapboxgl.Map} The Mapbox's Map
     */
    getMapboxMap() {
        return this.map;
    }

    /**
     * Returns the map's geographical centerpoint.
     * @returns {LngLat} The map's geographical centerpoint
     */
    getCenter() {
        return this.map.getCenter();
    }

    /**
     * Sets the map's geographical centerpoint. Equivalent to jumpTo({center: center}).
     * @param {LngLatLike} center - The centerpoint to set
     * @returns {Map} Returns itself to allow for method chaining
     */
    setCenter(center) {
        const me = this;

        me.trackObject();
        me.map.setCenter(center);
        return me;
    }

    /**
     * Returns the map's current zoom level.
     * @returns {number} The map's current zoom level
     */
    getZoom() {
        return this.map.getZoom();
    }

    /**
     * Sets the map's zoom level. Equivalent to jumpTo({zoom: zoom}).
     * @param {number} zoom - The zoom level to set (0-20)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setZoom(zoom) {
        const me = this;

        me.map.setZoom(zoom);
        return me;
    }

    /**
     * Returns the map's current bearing. The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * @returns {number} The map's current bearing
     */
    getBearing() {
        return this.map.getBearing();
    }

    /**
     * Sets the map's bearing (rotation). The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * Equivalent to jumpTo({bearing: bearing}).
     * @param {number} bearing - The desired bearing
     * @returns {Map} Returns itself to allow for method chaining
     */
    setBearing(bearing) {
        const me = this;

        me.trackObject();
        me.map.setBearing(bearing);
        return me;
    }

    /**
     * Returns the map's current pitch (tilt).
     * @returns {number} The map's current pitch, measured in degrees away from the
     *     plane of the screen
     */
    getPitch() {
        return this.map.getPitch();
    }

    /**
     * Sets the map's pitch (tilt). Equivalent to jumpTo({pitch: pitch}).
     * @param {number} pitch - The pitch to set, measured in degrees away from the
     *     plane of the screen (0-60)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setPitch(pitch) {
        const me = this;

        me.map.setPitch(pitch);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, pitch, and padding with an
     * animated transition between old and new values. The map will retain its current
     * values for any details not specified in options.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions and AnimationOptions
     * @returns {Map} Returns itself to allow for method chaining
     */
    easeTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.easeTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, animating the
     * transition along a curve that evokes flight. The animation seamlessly incorporates
     * zooming and panning to help the user maintain her bearings even after traversing
     * a great distance.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions, AnimationOptions, and a few additional
     *     options
     * @returns {Map} Returns itself to allow for method chaining
     */
    flyTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.flyTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, without an animated
     * transition. The map will retain its current values for any details not specified
     * in options.
     * @param {CameraOptions} options - Options object
     * @returns {Map} Returns itself to allow for method chaining
     */
    jumpTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.jumpTo(options);
        return me;
    }

    /**
     * Returns the current view mode.
     * @returns {string} Current view mode: 'both', 'ground' or 'underground'
     */
    getViewMode() {
        return this.viewMode;
    }

    /**
     * Sets the view mode.
     * @param {string} mode - View mode: 'both', 'ground' or 'underground'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setViewMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setViewMode(mode);
        }
        return me;
    }

    /**
     * Returns the current tracking mode.
     * @returns {string} Current tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     */
    getTrackingMode() {
        return this.trackingMode;
    }

    /**
     * Sets the tracking mode.
     * @param {string} mode - Tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setTrackingMode(mode) {
        const me = this;

        me._setTrackingMode(mode);
        return me;
    }

    /**
     * Returns the ID of the train or flight being tracked, or the IDs of the selected
     * stations.
     * @returns {string | Array<string>} The ID of the train or flight being tracked, or
     *     the IDs of the selected stations
     */
    getSelection() {
        const trackedObject = this.trackedObject;

        if (isVehicle(trackedObject)) {
            return trackedObject.id;
        } else if (isStation(trackedObject)) {
            return trackedObject.stations;
        }
    }

    /**
     * Sets the ID of the train or flight you want to track, or the station to select.
     * @param {string} id - ID of the train or flight to be tracked, or the station to
     *     be selected
     * @returns {Map} Returns itself to allow for method chaining
     */
    setSelection(id) {
        const me = this,
            selection = helpers.removePrefix(id),
            station = me.stations.get(selection);

        if (station) {
            me.trackObject(me.stationGroupLookup.get(station.group));
            delete me.initialSelection;
        } else if (!selection.match(/NRT|HND/)) {
            if (me.trainLoaded) {
                let activeTrain;

                for (const id of me.timetables.getConnectingTrainIds(selection)) {
                    if ((activeTrain = me.activeTrainLookup.get(id))) {
                        break;
                    }
                }
                if (activeTrain) {
                    me.trackObject(activeTrain);
                } else {
                    helpers.showNotification(me.container, me.dict['train-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        } else {
            if (me.flightLoaded) {
                const activeFlight = me.activeFlightLookup.get(selection);

                if (activeFlight) {
                    me.trackObject(activeFlight);
                } else {
                    helpers.showNotification(me.container, me.dict['flight-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        }

        return me;
    }

    /**
     * Returns the current clock mode.
     * @returns {string} Current clock mode: 'realtime' or 'playback'
     */
    getClockMode() {
        return this.clockMode;
    }

    /**
     * Sets the clock mode.
     * @param {string} mode - Clock mode: 'realtime' or 'playback'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setClockMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setClockMode(mode);
        }
        return me;
    }

    /**
     * Returns the current eco mode.
     * @returns {string} Current eco mode: 'normal' or 'eco'
     */
    getEcoMode() {
        return this.ecoMode;
    }

    /**
     * Sets the eco mode.
     * @param {string} mode - Eco mode: 'normal' or 'eco'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setEcoMode(mode) {
        const me = this;

        me._setEcoMode(mode);
        return me;
    }

    /**
     * Adds a layer to the map.
     * @param {Object | CustomLayerInterface | GeoJsonLayerInterface | ThreeLayerInterface | Tile3DLayerInterface} layer
     *     - The layer to add, conforming to either the Mapbox Style Specification's
     *     layer definition, the CustomLayerInterface specification, the
     *     GeoJsonLayerInterface specification, the ThreeLayerInterface specification
     *     or the Tile3DLayerInterface specification
     * @param {string} beforeId - The ID of an existing layer to insert the new
     *     layer before
     * @returns {Map} Returns itself to allow for method chaining
     */
    addLayer(layer, beforeId) {
        const me = this,
            type = layer.type;

        if (type === 'three') {
            new ThreeLayer(layer).onAdd(me, beforeId);
        } else if (type === 'geojson') {
            new GeoJsonLayer(layer).onAdd(me, beforeId);
        } else if (type === 'tile-3d') {
            new Tile3DLayer(layer).onAdd(me, beforeId);
            me.layerVisibility.tile3d.set(layer.id, 'visible');
            me.updateLayerVisibility();
        } else {
            me.map.addLayer(layer, beforeId || 'poi');
        }
        return me;
    }

    /**
     * Removes the layer with the given ID from the map.
     * @param {string} id - ID of the layer to remove
     * @returns {Map} Returns itself to allow for method chaining
     */
    removeLayer(id) {
        const me = this,
            tile3d = me.layerVisibility.tile3d;

        me.map.removeLayer(id);
        if (tile3d.has(id)) {
            tile3d.delete(id);
            me.updateLayerVisibility();
        }
        return me;
    }

    /**
     * Sets the visibility of the layer.
     * @param {string} layerId - The ID of the layer to set the visibility in.
     * @param {string} visibility - Whether this layer is displayed.
     *     'visible' and 'none' are supported.
     * @returns {Map} Returns itself to allow for method chaining
     */
    setLayerVisibility(layerId, visibility) {
        const me = this,
            {model, tile3d} = me.layerVisibility;
        let value = visibility;

        if (model.has(layerId)) {
            model.set(layerId, visibility);
            if (tile3d.values().some(v => v === 'visible')) {
                value = 'none';
            }
        } else if (tile3d.has(layerId)) {
            tile3d.set(layerId, visibility);
            me.updateLayerVisibility();
        }
        me.map.setLayoutProperty(layerId, 'visibility', value);
        return me;
    }

    /**
     * Returns a MercatorCoordinate object that represents the initial centerpoint
     * of the map as the origin of the mercator coordinates.
     * @returns {MercatorCoordinate} The origin of the mercator coordinates
     */
    getModelOrigin() {
        return this.modelOrigin;
    }

    /**
     * Returns the scale to transform into MercatorCoordinate from coordinates in
     * real world units using meters. This provides the distance of 1 meter in
     * MercatorCoordinate units at the initial centerpoint of the map.
     * @returns {number} The scale to transform into MercatorCoordinate from
     *     coordinates in real world units using meters
     */
    getModelScale() {
        return this.modelOrigin.meterInMercatorCoordinateUnits();
    }

    /**
     * Projects a LngLat to a MercatorCoordinate, and returns the translated
     * mercator coordinates with the initial centerpoint of the map as the origin.
     * @param {LngLatLike} lnglat - The location to project
     * @param {number} altitude - The altitude in meters of the position
     * @returns {Object} The translated mercator coordinates with the initial
     *     centerpoint of the map as the origin
     */
    getModelPosition(lnglat, altitude) {
        const me = this,
            coord = MercatorCoordinate.fromLngLat(lnglat, altitude);

        return {
            x: coord.x - me.modelOrigin.x,
            y: -(coord.y - me.modelOrigin.y),
            z: coord.z - me.modelOrigin.z
        };
    }

    /**
     * Checks if the background color of the map is dark.
     * @returns {boolean} True if the background color of the map is dark
     */
    hasDarkBackground() {
        return helpersMapbox.hasDarkBackground(this.map);
    }

    /**
     * Adds a data source to the map. If a data source with the same ID already
     * exists, it is replaced.
     * @param {Object} source - The data source to add. See the Map constructor
     *     for the object structure
     * @returns {Map} Returns itself to allow for method chaining
     */
    addDataSource(source) {
        const me = this,
            dataSource = normalizeDataSource(source, me.secrets),
            index = me.dataSources.findIndex(({id}) => id === dataSource.id);

        if (index >= 0) {
            me.dataSources[index] = dataSource;
        } else {
            me.dataSources.push(dataSource);
        }
        me.refreshDataSource(dataSource);
        return me;
    }

    /**
     * Removes the data source with the given ID from the map.
     * @param {string} id - ID of the data source to remove
     * @returns {Map} Returns itself to allow for method chaining
     */
    removeDataSource(id) {
        const me = this,
            index = me.dataSources.findIndex(source => source.id === id);

        if (index >= 0) {
            const [removed] = me.dataSources.splice(index, 1);

            me.refreshDataSource(removed);
        }
        return me;
    }

    /**
     * Reloads the dynamic data affected by the given data source, based on which
     * URLs it carries. GTFS bus data is refreshed immediately; train and flight
     * data are refreshed only once the map has been initialized, otherwise they
     * are picked up on the next real-time update.
     * @param {Object} source - The data source that was added or removed
     */
    refreshDataSource(source) {
        const me = this;

        if (source.gtfsUrl) {
            me.refreshBusData();
        }
        if (me.initialized) {
            if (source.trainUrl || source.trainInfoUrl) {
                me.refreshRealtimeTrainData();
            }
            if (source.flightUrl || source.atisUrl) {
                me.refreshRealtimeFlightData();
            }
        }
    }

    initData(data) {
        const me = this,
            featureLookup = me.featureLookup = new Map(),
            stationGroupLookup = me.stationGroupLookup = new Map();

        me.featureCollection = data.featureCollection;
        me.congestion = data.congestionData;

        me.stations = new Dataset(Station);
        me.railDirections = new Dataset(RailDirection, data.railDirectionData);
        me.railways = new Dataset(Railway, data.railwayData, {
            stations: me.stations,
            railDirections: me.railDirections
        });
        me.pois = new Dataset(POI, data.poiData);
        me.stations.load(data.stationData, {
            stations: me.stations,
            railways: me.railways,
            railDirections: me.railDirections,
            pois: me.pois
        });

        // Build feature lookup dictionary and update feature properties
        featureEach(me.featureCollection, feature => {
            const properties = feature.properties,
                {group, altitude, section} = properties;

            if (section) {
                // Track section IDs are '<railway ID>.<n>'
                properties.railway = section.replace(/\.[^.]*$/, '');
            }
            if (properties.type === 1) {
                // stations
                // Which railways the station serves, as a searchable string so a
                // Mapbox filter expression can test membership ('|' delimited to
                // keep Seoul.Line1 from matching Seoul.Line1Gyeongbu).
                properties.railways = `|${properties.ids.map(id => id.replace(/\.[^.]*$/, '')).join('|')}|`;
                featureLookup.set(`${group}.${properties.zoom}`, feature);
                if (!stationGroupLookup.has(group)) {
                    stationGroupLookup.set(group, {
                        id: group,
                        type: 'station',
                        stations: properties.ids.map(id => me.stations.get(id)),
                        layer: altitude === 0 ? 'ground' : 'underground'
                    });
                }
            } else if (!(altitude <= 0)) {
                // airways and railways (no railway sections)
                featureLookup.set(properties.id, feature);
                helpersGeojson.updateDistances(feature);
            }
        });

        for (const station of me.stations.getAll()) {
            if (station.alternate) {
                for (const layer of ['og', 'ug']) {
                    const key = station.group.replace(/.g$/, layer),
                        stationGroup = stationGroupLookup.get(key);

                    if (stationGroup) {
                        if (!stationGroup.hidden) {
                            stationGroup.hidden = [];
                        }
                        stationGroup.hidden.push(station);
                    }
                }
            }
        }

        me.trainTypes = new Dataset(TrainType, data.trainTypeData);
        me.trainVehicleTypes = new Dataset(TrainVehicleType, data.trainVehicleData);

        me.lastTimetableRefresh = me.clock.getTime('03:00');
        me.dataReferences = {
            railways: me.railways,
            stations: me.stations,
            railDirections: me.railDirections,
            trainTypes: me.trainTypes,
            trainVehicleTypes: me.trainVehicleTypes
        };
        me.timetables = new TrainTimetables(data.timetableData, me.dataReferences);

        me.operators = new Dataset(Operator, data.operatorData);
        me.airports = new Dataset(Airport, data.airportData);
        me.flightStatuses = new Dataset(FlightStatus, data.flightStatusData);

        me.activeTrainLookup = new Map();
        me.standbyTrainLookup = new Map();
        me.realtimeTrains = new Set();
        me.adTrains = new Set();
        me.activeFlightLookup = new Map();
        me.flightLookup = new Map();
    }

    initialize() {
        const me = this,
            {lang, dict, clock, map, container, featureCollection} = me,
            initialZoom = map.getZoom(),
            layerZoom = me.layerZoom = getLayerZoom(initialZoom);

        if (map.loaded()) {
            hideLoader(container);
        } else {
            map.once('load', () => {
                hideLoader(container);
            });
        }

        // Deprecated
        // me.objectUnit = Math.max(getObjectScale(initialZoom) * .19, .02);

        me.trafficLayer = new TrafficLayer({id: 'traffic'});

        map.setLayoutProperty('poi', 'text-field', [
            'coalesce',
            ['get', `name_${lang}`],
            ['get', 'name_en'],
            ['get', 'name']
        ]);

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const commonProps = {
                type: 'geojson',
                lineWidthUnits: 'pixels',
                lineWidthScale:
                    zoom === 13 ? helpers.clamp(Math.pow(2, initialZoom - 12), .125, 1) :
                    zoom === 18 ? helpers.clamp(Math.pow(2, initialZoom - 19), 1, 8) : 1,
                parameters: {depthTest: false},
                minzoom: zoom <= 13 ? 0 : zoom,
                maxzoom: zoom >= 18 ? 24 : zoom + 1
            };

            for (const key of ['marked', 'selected']) {
                me.addLayer(Object.assign({}, commonProps, {
                    id: `stations-${key}-${zoom}`,
                    getLineWidth: 12,
                    getLineColor: [255, 255, 255],
                    getFillColor: [255, 255, 255],
                    visible: false
                }), 'building-3d');
            }

            for (const key2 of ['ug', 'routeug', 'routeog']) {
                for (const key1 of ['railways', 'stations']) {
                    const type = key1 === 'railways' ? 0 : 1,
                        filter = p => p.zoom === zoom && p.type === type && p.altitude < 0 && !me._isFeatureHidden(p);

                    me.addLayer(Object.assign({}, commonProps, {
                        id: `${key1}-${key2}-${zoom}`,
                        data: key2 === 'ug' ?
                            helpersGeojson.featureFilter(featureCollection, filter) :
                            helpersGeojson.emptyFeatureCollection()
                    }, {
                        'railways': {
                            filled: false,
                            getLineWidth: d => d.properties.width,
                            getLineColor: d => helpers.colorToRGBArray(d.properties.color)
                        },
                        'stations': {
                            getLineWidth: 4,
                            getFillColor: [255, 255, 255, 179]
                        }
                    }[key1], {
                        'ug': {
                            opacity: .0625,
                            pickable: key1 === 'stations',
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0.0625,
                                'mt3d:opacity-route': 0.005,
                                // 'both' 는 지하도 그대로 읽히게 둔다. 살짝만 덜 진하게 해
                                // 지상과 구분하고, 위치는 음수 고도가 알려준다.
                                'mt3d:opacity-both': 0.85,
                                'mt3d:opacity-underground': 1,
                                'mt3d:opacity-underground-route': 0.005
                            }
                        },
                        'routeug': {
                            opacity: .0625,
                            visible: false,
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0.125,
                                'mt3d:opacity-underground': 1
                            }
                        },
                        'routeog': {
                            visible: false,
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-underground': 0.25
                            }
                        }
                    }[key2]), 'building-3d');
                }
            }
        }

        resetCursor(map.__deck);

        map.addSource('odpt', {
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p => p.altitude === 0)
        });

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const interpolate = ['interpolate', ['exponential', 2], ['zoom']],
                width = ['get', 'width'],
                color = ['get', 'color'],
                lineWidth =
                    zoom === 13 ? [...interpolate, 9, ['/', width, 8], 12, width] :
                    zoom === 18 ? [...interpolate, 19, width, 22, ['*', width, 8]] :
                    width;

            for (const key of ['railways', 'stations', 'stations-outline']) {
                map.addLayer({
                    id: `${key}-og-${zoom}`,
                    type: key === 'stations' ? 'fill' : 'line',
                    source: 'odpt',
                    filter: me._getGroundFilter(key, zoom),
                    layout: {
                        visibility: zoom === layerZoom ? 'visible' : 'none'
                    },
                    paint: {
                        'railways': {
                            'line-color': color,
                            'line-width': lineWidth
                        },
                        'stations': {
                            'fill-color': color,
                            'fill-opacity': .7
                        },
                        'stations-outline': {
                            'line-color': ['get', 'outlineColor'],
                            'line-width': lineWidth
                        }
                    }[key],
                    metadata: {
                        'mt3d:opacity-effect': true,
                        'mt3d:opacity': 1,
                        'mt3d:opacity-route': 0.1,
                        'mt3d:opacity-underground': 0.25,
                        'mt3d:opacity-underground-route': 0.1
                    }
                }, 'building-3d');
            }
        }

        // mt3d 레이어(위의 노선·역 + 이 차량 레이어)는 전부 'building-3d' **앞**에
        // 쌓는다. 건물보다 뒤('trees')에 넣으면 건물이 깊이버퍼를 먼저 채워 뒤에 있는
        // 열차가 통째로 사라진다 - 건물이 반투명이어도 마찬가지다. MapLibre 의 반투명
        // fill-extrusion 도 깊이를 ReadWrite 로 쓴다(drawFillExtrusion).
        // 순서를 뒤집으면 열차가 먼저 색+깊이를 쓰고 건물이 그 위에 알파로 얹히므로
        // **앞 건물에 가린 열차는 흐리게 비치고, 앞에 있는 열차는 그대로 보인다** -
        // 깊이 단서를 잃지 않는다. 비치는 정도는 style.json 의
        // fill-extrusion-opacity 하나로 조절한다.
        //
        // ⚠️ 차량은 **그룹의 맨 뒤**여야 한다. 역·노선 deck 레이어는
        // `depthTest: false` 라 깊이와 무관하게 나중에 그린 쪽이 이긴다 - 차량을
        // 그룹 앞으로 옮기면 역 폴리곤이 열차 위에 덮인다(실측).
        me.addLayer(me.trafficLayer, 'building-3d');

        const routeData = [],
            colorData = [];

        for (const {id, color} of me.railways.getAll()) {
            routeData.push({
                id,
                // zoom 별 전체 라인은 좌표가 동일하므로 하나만 저장한다 (features.js 참조).
                // route-data 가 배열 길이만큼 텍스처 엔트리를 만들므로 길이 6은 유지한다.
                feature: new Array(6).fill(me.featureLookup.get(`${id}.18`))
            });
            // color0 = 지붕 뒤 삼각형(혼잡도)의 기본값이므로 지붕색으로 채운다.
            // 노선색을 그대로 두면 차종 데이터가 없는 노선만 지붕에 삼각형이 둘 보인다
            // (train-vehicles.json 쪽은 color0 이 이미 흰색이다).
            colorData.push({id, color: [CAR_ROOF_COLOR, color, color, color]});
        }
        for (const [id, feature] of me.featureLookup) {
            if (feature.properties.altitude > 0) {
                routeData.push({id, feature});
            }
        }
        for (const {id, color} of me.trainVehicleTypes.getAll()) {
            colorData.push({id, color});
        }
        for (const {id, color, tailcolor} of me.operators.getAll()) {
            colorData.push({id, color: [color, tailcolor]});
        }

        me.trafficLayer.addRouteGroup(routeData);
        me.trafficLayer.addColorGroup(colorData);

        /* For development
        me.addLayer({
            id: `airway-og-`,
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p =>
                p.type === 0 && p.altitude > 0
            ),
            filled: false,
            getLineWidth: d => d.properties.width,
            getLineColor: d => helpers.colorToRGBArray(d.properties.color),
            lineWidthUnits: 'pixels',
            lineWidthScale: 1,
            opacity: .0625,
            transitions: {opacity: configs.transitionDuration},
            parameters: {depthTest: false}
        });
        */

        me.styleOpacities = helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect');

        // layerVisibility stores the visibility of model layers (including fill-extrusion layers) and
        // tile-3d layers, and is used to control the display of each group on an exclusive basis.
        me.layerVisibility = {model: new Map(), tile3d: new Map()};
        for (const {id, type} of map.getStyle().layers) {
            if (type === 'model' || type === 'fill-extrusion' || id.startsWith('hd-')) {
                me.layerVisibility.model.set(id, 'visible');
            }
        }

        const datalist = helpers.createElement('datalist', {id: 'stations'}, document.body);
        const stationTitleLookup = me.stationTitleLookup = new Map();

        for (const l of [lang, 'en']) {
            for (const railway of me.railways.getAll()) {
                for (const station of railway.stations) {
                    const {title, utitle} = station,
                        stationTitle = (utitle && utitle[l]) || helpers.normalize(title[l] || title.en),
                        key = stationTitle.toUpperCase();

                    if (!stationTitleLookup.has(key)) {
                        helpers.createElement('option', {value: stationTitle}, datalist);
                        stationTitleLookup.set(key, station);
                    }
                }
            }
        }

        // 화면 겹층. mapbox 의 코너 컨트롤에 다 밀어넣으면 모바일에서 오른쪽 한 줄이
        // 화면 높이의 70% 를 먹는다(실측 버튼 15개 / 586px). 레퍼런스(네이버 지도·windy)는
        // 상시 노출을 5개 안팎으로 두고 검색은 상단 전폭, 시간은 하단 전폭, 나머지는
        // 진입점 하나 뒤에 둔다. 그 배치를 따른다.
        const statusBar = me.statusBar = helpers.createElement('div', {className: 'status-bar'}, container);
        let searchElement;

        // 검색 후보는 바로 위에서 채운 stationTitleLookup 이 전부다. 버스 빌드는
        // dataUrl 이 비어 있어 역이 0개라 datalist 가 텅 비고, 무엇을 쳐도 안 맞는다
        // (bus.kgenots.com 실측: #stations 옵션 0개 vs metro 1,307개). 검색할 게
        // 없으면 메뉴에 항목을 만들지 않는다.
        if (me.searchControl && me.stationTitleLookup.size) {
            const control = new SearchControl({
                title: me.dict['search'],
                placeholder: me.dict['station-name'],
                list: 'stations',
                eventHandler: ({value}) => {
                    const station = me.stationTitleLookup.get(value.toUpperCase());

                    if (station && station.coord) {
                        me.markObject();
                        me.trackObject(me.stationGroupLookup.get(station.group));
                        return true;
                    }
                }
            });

            // 검색은 지도 위에 상시로 두지 않는다. 역을 찾는 일은 자주 하는 동작이
            // 아니고, 상시 노출은 화면만 먹는다. 메뉴를 펼쳤을 때 그 안에 보인다.
            // 메뉴는 아래에서 만들므로 요소만 받아두고 나중에 넣는다.
            searchElement = control.onAdd(map);
            control._button.classList.add('expanded');
            control._searchBox.classList.remove('disabled');
        }

        if (me.navigationControl) {
            const control = me.navControl = new NavigationControl();

            control._setButtonTitle = function(button) {
                const {_zoomInButton, _zoomOutButton, _compass} = this,
                    title = button === _zoomInButton ? dict['zoom-in'] :
                    button === _zoomOutButton ? dict['zoom-out'] :
                    button === _compass ? dict['compass'] : '';

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            // 줌은 엄지가 닿는 우하단으로. 우상단이면 한 손 조작이 안 된다.
            map.addControl(control, 'bottom-right');
        }

        if (me.fullscreenControl) {
            const control = new FullscreenControl({container});

            control._updateTitle = function() {
                const button = this._fullscreenButton,
                    title = dict[this._isFullscreen() ? 'exit-fullscreen' : 'enter-fullscreen'];

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            // 전체화면은 우하단 줌 위에. 우상단은 메뉴가 쓴다.
            map.addControl(control, 'bottom-right');
        }

        if (me.clockControl) {
            const control = me.clockCtrl = new ClockControl({lang, dict, clock});

            control.on('change', me.onClockChange.bind(me));
            statusBar.appendChild(control.onAdd(map));
        }

        me.railwayPanel = new RailwayPanel();
        me.trackingModePanel = new TrackingModePanel();
        me.aboutPanel = new AboutPanel();

        // 상태 바의 세그먼트. 서로 배타인 상태 세 쌍을 버튼 6개가 아니라 pill 3개로 묶는다.
        // 클래스명은 기존 것을 그대로 쓴다 — updateUndergroundButton 같은 상태 동기화
        // 메서드가 이 이름으로 요소를 찾아 제목과 활성 표시를 갱신하기 때문이다.
        if (me.modeControl) {
            const segments = me.modeSegments = helpers.createElement('div', {className: 'mode-segments'}, statusBar);

            for (const {className, title, labels, eventHandler} of [{
                className: 'maplibregl-ctrl-underground',
                title: dict['enter-ground'],
                // 지상+지하 -> 지상 -> 지하 -> 다시 지상+지하. pill 은 지금 상태를 적는다.
                labels: [dict['ground'], dict['underground'], dict['both-levels']],
                eventHandler: () => me._setViewMode({
                    both: 'ground', ground: 'underground', underground: 'both'
                }[me.viewMode])
            }, {
                className: 'maplibregl-ctrl-playback',
                title: dict['enter-playback'],
                labels: [dict['realtime'], dict['playback']],
                eventHandler: () => me._setClockMode(me.clockMode === 'realtime' ? 'playback' : 'realtime')
            }, {
                className: 'maplibregl-ctrl-congestion',
                title: dict['enter-congestion'],
                labels: [dict['plain'], dict['congestion-short']],
                eventHandler: () => me._setCongestionMode(!me.congestionMode)
            // 혼잡도 원본은 지하철 1~8호선 전용이다. 버스 빌드는 dataUrl 이 비어 있어
            // loadStaticData 가 빈 배열을 돌려주므로 stations 가 없다 - 그 상태로 pill 을
            // 그리면 눌러도 아무 색이 안 칠해지는 죽은 컨트롤이 된다 (bus.kgenots.com
            // 실측: 토글은 되는데 /data/congestion.json.gz 가 404).
            }].filter(({className}) => className !== 'maplibregl-ctrl-congestion' ||
                    (me.congestion && me.congestion.stations))) {
                const button = helpers.createElement('button', {className: `segment ${className}`, type: 'button', title}, segments);

                helpers.createElement('span', {className: 'segment-icon'}, button);
                helpers.createElement('span', {className: 'segment-off', innerHTML: labels[0]}, button);
                helpers.createElement('span', {className: 'segment-on', innerHTML: labels[1]}, button);
                if (labels[2]) {
                    helpers.createElement('span', {className: 'segment-both', innerHTML: labels[2]}, button);
                }
                button.addEventListener('click', eventHandler);
            }

            // 기본 뷰 모드가 '지상'이 아니므로 만든 직후 한 번 맞춘다. 이 pill 은
            // 세 상태라 클래스가 없으면 '지상'으로 보인다.
            me.updateUndergroundButton(me.viewMode);
        }

        // 모드는 여태 버튼을 누를 때만 지도에 반영됐다. 기본값이 지상이라 그래도
        // 맞았지만 이제는 아니다 — 첫 프레임부터 기본 모드로 그린다. 레이어와
        // 트래픽 레이어가 모두 만들어진 뒤라야 하므로 여기서 부른다.
        if (me.viewMode !== 'ground') {
            me.refreshMap();
        }

        // 나머지는 전부 우하단 진입점 하나 뒤로.
        const fab = me.fab = helpers.createElement('button', {
            className: 'fab', type: 'button', title: dict['more']
        }, container);
        const fabMenu = me.fabMenu = helpers.createElement('div', {className: 'fab-menu hidden'}, container);

        const setMenu = open => {
            fabMenu.classList.toggle('hidden', !open);
            fab.classList.toggle('fab-open', open);
        };

        fab.addEventListener('click', () => setMenu(fabMenu.classList.contains('hidden')));
        map.on('click', () => {
            setMenu(false);
            // 숨김을 푸는 스위치도 같이 사라지므로 지도를 건드리면 돌아온다.
            // 키보드가 없는 기기에는 이것이 유일한 복귀 경로다.
            me._setUIHidden(false);
        });
        me.onKeyDown = event => {
            const {key, target} = event;

            // 입력 중인 글자를 단축키로 가로채지 않는다 (역 검색창).
            if (target && target.closest && target.closest('input, textarea, [contenteditable]')) {
                return;
            }
            if (key === 'Escape') {
                me._setUIHidden(false);
                return;
            }
            // 시간 배속: + 빠르게, - 느리게, 0 실시간. 배속 버튼은 재생 모드에만
            // 있으므로 필요하면 모드를 먼저 바꾼다. 값과 라벨·비활성 표시를 한 곳에서만
            // 다루도록 시계 컨트롤의 버튼을 그대로 누른다 — 양 끝(1배·600배)에서
            // 버튼이 disabled 라 한계도 그쪽이 지킨다.
            if (key !== '0' && key !== '+' && key !== '=' && key !== '-') {
                return;
            }
            event.preventDefault();
            event.stopPropagation();

            if (key === '0') {
                me._setClockMode('realtime');
                return;
            }
            me._setClockMode('playback');

            const button = container.querySelector(key === '-' ? '#speed-decrease-button' : '#speed-increase-button');

            if (button) {
                button.click();
            }
        };
        // 캡처 단계로 받는다. mapbox 의 keyboard 핸들러가 캔버스에서 +/- 를 줌으로
        // 쓰기 때문에, 버블링으로 받으면 배속과 줌이 같이 일어난다. 캡처에서 받아
        // 우리가 쓰는 키만 전파를 끊는다 — 방향키·회전은 그대로 둔다.
        document.addEventListener('keydown', me.onKeyDown, true);

        if (searchElement) {
            fabMenu.appendChild(searchElement);
        }

        for (const {className, title, eventHandler} of [{
            className: 'maplibregl-ctrl-cinema',
            title: dict['enter-cinema'],
            eventHandler: () => me._setCinemaMode(!me.cinemaMode)
        }, {
            className: 'maplibregl-ctrl-hide-ui',
            title: dict['hide-ui'],
            eventHandler: () => me._setUIHidden(true)
        }, {
            className: 'maplibregl-ctrl-nearby',
            title: dict['show-my-location'],
            eventHandler: () => me.showCurrentLocation()
        }, {
            className: 'maplibregl-ctrl-railways',
            title: dict['select-railways'],
            eventHandler: () => me.railwayPanel.addTo(me)
        }, {
            className: 'maplibregl-ctrl-tracking-mode',
            title: dict['select-tracking-mode'],
            eventHandler: () => me.trackingModePanel.addTo(me)
        }, {
            className: 'maplibregl-ctrl-eco',
            title: dict['enter-eco'],
            eventHandler: () => me._setEcoMode(me.ecoMode === 'eco' ? 'normal' : 'eco')
        }, {
            className: 'maplibregl-ctrl-about',
            title: dict['about'],
            eventHandler: () => me.aboutPanel.addTo(me)
        }]) {
            const item = helpers.createElement('button', {className: `fab-item ${className}`, type: 'button'}, fabMenu);

            helpers.createElement('span', {className: 'maplibregl-ctrl-icon'}, item);
            helpers.createElement('span', {className: 'fab-label', innerHTML: title}, item);
            item.addEventListener('click', () => {
                setMenu(false);
                eventHandler();
            });
        }

        // 버튼이 붙은 뒤에 켜짐 표시와 타이틀을 상태에 맞춘다
        me._setCongestionMode(me.congestionMode);

        map.on('mousemove', e => {
            me.markObject(me.pickObject(e.point));
        });

        map.on('mouseout', () => {
            me.markObject();
        });

        map.on('click', e => {
            const object = me.pickObject(e.point);

            me.markObject(object);
            me.trackObject(object);

            // 정류장은 pickObject 에 넣지 않는다. 그 함수는 위 mousemove 에서도
            // 불리므로 넣으면 **마우스를 올리는 것만으로 도착정보를 조회**하게 되고
            // (하루 1,000콜짜리 쿼터다), 열차에서 이미 같은 함정을 겪었다
            // (pickObject 가 hover 에서 carInstanceID 를 덮어쓰던 건).
            //
            //
            // 패널은 detailPanel 에 얹지 않고 따로 든다. trackObject 는 '추적 대상이
            // 있을 때만' detailPanel 을 정리하는데, 정류장은 추적 대상이 아니라서
            // 얹으면 다음 클릭에 안 치워진 채 새 패널이 위에 쌓인다.
            me.hideBusStopArrivals();
            if (!object) {
                me.showBusStopArrivals(e.point);
            }

            // For development
            console.log(e.lngLat);
        });

        // 트래킹이 만든 카메라 이동은 jumpTo 가 {tracking: true} 로 태그한다. 태그가
        // 없으면 사용자가 직접 돌린 것이므로 그 값을 기억해 _jumpTo 가 매 프레임
        // 덮어쓰지 않게 한다 (zoom 이 baseZoom 으로 하는 것과 같은 방식).
        for (const type of ['rotate', 'pitch']) {
            map.on(type, e => {
                if (!e.tracking) {
                    me.updateCameraOverride();
                }
            });
        }

        map.on('zoom', e => {
            if (!e.tracking) {
                me.updateBaseZoom();
            }

            const zoom = map.getZoom(),
                prevLayerZoom = me.layerZoom,
                layerZoom = me.layerZoom = getLayerZoom(zoom);

            if (zoom < 13) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 12), .125, 1);

                for (const id of ['stations-marked-13', 'stations-selected-13', 'railways-ug-13', 'stations-ug-13', 'railways-routeug-13', 'stations-routeug-13', 'railways-routeog-13', 'stations-routeog-13']) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            } else if (zoom > 19) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 19), 1, 8);

                for (const id of ['stations-marked-18', 'stations-selected-18', 'railways-ug-18', 'stations-ug-18', 'railways-routeug-18', 'stations-routeug-18', 'railways-routeog-18', 'stations-routeog-18']) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            }

            // Deprecated
            // me.objectUnit = Math.max(getObjectScale(zoom) * .19, .02);

            if (prevLayerZoom !== layerZoom) {
                for (const key of ['railways', 'stations', 'stations-outline']) {
                    me.setLayerVisibility(`${key}-og-${prevLayerZoom}`, 'none');
                    me.setLayerVisibility(`${key}-og-${layerZoom}`, 'visible');
                }

                if (e.tracking) {
                    me.prevLayerZoom = prevLayerZoom;
                } else {
                    delete me.prevLayerZoom;
                }
            }
        });

        map.on('render', () => {
            const object = me.getPopupObject();

            // Popup for a 3D object needs to be updated every time
            // because the adjustment for altitude is required
            //
            // 단, 추적 중인 대상은 여기서 만지지 않는다. 애니메이션 루프가 _jumpTo
            // 직후에 갱신하므로, 여기서 또 하면 한 프레임에 setLngLat 이 두 번 들어간다.
            // AnimatedPopup 이 map._requestDomTask 를 가로채 DOM 쓰기를 지연 큐에
            // 넣기 때문에(mapbox-gl-animated-popup) 중복 쓰기가 프레임마다 다른 값으로
            // 적용되고, 카메라가 매 프레임 도는 helicopter·시네마에서 말풍선이 위아래로
            // 떨린다. mapbox Popup 은 이미 map.on('move') 로 저장된 lngLat 기준 재배치를
            // 하므로 카메라만 움직일 때는 우리가 손댈 필요가 없다.
            if (object && object !== me.trackedObject) {
                me.updatePopup();
            }
            me.updateAdTrainPopup();
        });

        animation.init();

        animation.start({
            callback: () => {
                const clock = me.clock,
                    now = clock.getTime(),
                    {minDelay, refreshInterval, realtimeCheckInterval} = configs;

                if (now - me.lastTimetableRefresh >= 86400000) {
                    me.refreshTrainTimetableData();
                    me.refreshBusData(true);
                    me.lastTimetableRefresh = clock.getTime('03:00');
                }

                // Remove all trains if the page has been invisible for certain amount of time
                if (Date.now() - me.lastFrameRefresh >= configs.refreshTimeout) {
                    me.stopAll();
                }
                me.lastFrameRefresh = Date.now();

                const clockRefresh = Math.floor((now - minDelay) / refreshInterval) !== Math.floor(me.lastRefresh / refreshInterval);

                if (me.clockControl) {
                    me.clockCtrl.setTrainCount(me.activeTrainLookup.size);
                }
                if (clockRefresh) {
                    me.refreshCongestionColors();
                }

                if (clockRefresh) {
                    if (me.searchMode === 'none' && me.clockMode === 'playback' && !me.removing) {
                        me.refreshTrains();
                        me.refreshFlights();
                        me.refreshBuses();
                        if (isStation(me.trackedObject)) {
                            me.detailPanel.updateContent();
                        }
                    }
                    me.lastRefresh = now - minDelay;
                }

                // Refresh the sun-position-dependent lighting when the clock advances
                // or the map pans far enough, but no more than about once per second
                // of real time: each refresh re-evaluates the lighting of every model
                // instance, so fast playback would otherwise trigger it many times a
                // second. The sun barely moves in a second, so the visual cost is nil.
                const center = map.getCenter(),
                    elapsed = Date.now() - me.lastSunlightRefresh,
                    moved = helpersMapbox.hasCenterMoved(center, me.lastSunlightCenter);

                if ((clockRefresh || moved) && elapsed >= 1000) {
                    // Animate on a large pan (a one-step change in the sun position) or
                    // when the clock advances beyond what continuous playback over the
                    // elapsed real time would explain (a clock-panel jump, mode switch,
                    // etc.). Continuous playback matches speed * elapsed, so the 2x
                    // margin keeps it snapping at any speed.
                    const transition = moved || Math.abs(now - me.lastSunlightTime) >= clock.speed * elapsed * 2;

                    helpersMapbox.setSunlight(map, now, me.viewMode === 'underground' ? 0 : 1, false, transition);
                    me.lastSunlightCenter = center;
                    me.lastSunlightTime = now;
                    me.lastSunlightRefresh = Date.now();
                }

                // Fire 'light' whenever the rendered light actually changes (the frame
                // after an instant change, and every frame of a transition), so the
                // layers track it. A cheap signature (brightness + sun
                // direction) avoids firing when nothing changed.
                const directional = helpersMapbox.getDirectionalLight(map),
                    brightness = helpersMapbox.getBrightness(map),
                    signature = `${brightness} ${directional.direction[0]} ${directional.direction[1]}`;

                if (signature !== me.lastLightSignature) {
                    me.lastLightSignature = signature;
                    me.fire({
                        type: 'light',
                        directional,
                        ambient: helpersMapbox.getAmbientLight(map),
                        brightness
                    });
                }

                if (Math.floor((now - minDelay) / realtimeCheckInterval) !== Math.floor(me.lastRealtimeCheck / realtimeCheckInterval)) {
                    if (me.searchMode === 'none' && me.clockMode === 'realtime' && !me.removing) {
                        me.refreshRealtimeTrainData();
                        me.refreshRealtimeFlightData();
                        me.refreshRealtimeBusData();
                        if (isStation(me.trackedObject)) {
                            me.detailPanel.updateContent();
                        }
                    }
                    me.lastRealtimeCheck = now - minDelay;
                }

                if ((me.ecoMode === 'normal' && map._loaded) || Date.now() - me.lastRepaint >= 1000 / me.ecoFrameRate) {
                    let changed;

                    me.trafficLayer.setTimeOffset(clock.getTimeOffset());

                    if (me.markedObject && isVehicle(me.markedObject)) {
                        changed = me.updateObjectPosition(me.markedObject);
                        if (changed.standing !== undefined) {
                            me.updatePopup({setHTML: true});
                        }
                    }

                    if (me.trackedObject && isVehicle(me.trackedObject)) {
                        if (me.trackedObject !== me.markedObject) {
                            changed = me.updateObjectPosition(me.trackedObject);
                        }
                        // 추적 중인 열차가 터널을 드나들면 모드를 따라 바꾸지만,
                        // 'both' 에서는 이미 둘 다 보이므로 그대로 둔다.
                        if (changed.viewMode && me.viewMode !== 'both') {
                            me._setViewMode(changed.viewMode);
                        }
                        if (!me.viewAnimationID && !map._zooming && !map._rotating && !map._pitching) {
                            const {coord, altitude, bearing} = me.getTrackedCameraTarget();

                            me._jumpTo({center: coord, altitude, bearing, bearingFactor: .02});
                            if (me.markedObject === me.trackedObject || !me.markedObject) {
                                // This need to be called right after jumpTo() to prevent jittering
                                me.updatePopup();
                            }
                        }
                    }

                    for (const train of me.adTrains) {
                        me.updateObjectPosition(train);
                    }

                    if (!isVehicle(me.trackedObject)) {
                        if (me.trackedObject) {
                            me.refreshStationOutline();
                        }
                    }

                    map.triggerRepaint();
                    me.lastRepaint = Date.now();
                }
            }
        });

        me.initialized = true;
        me.fire({type: 'initialized'});
    }

    _jumpTo(options) {
        const me = this,
            {map, trackingMode, trackingParams, cameraOverride} = me,
            frameRateFactor = me.ecoMode === 'normal' || me.viewAnimationID ? me.frameRateFactor : 60 / me.ecoFrameRate,
            now = performance.now(),
            currentZoom = map.getZoom(),
            currentBearing = map.getBearing(),
            currentPitch = map.getPitch(),
            scrollZooming = map.scrollZoom._active;
        let zoom, pitch,
            {center, altitude, bearing, easeOutFactor, easeInFactor, bearingFactor} = options;

        if (trackingMode === 'position') {
            zoom = me.baseZoom;
            bearing = currentBearing;
            pitch = currentPitch;
        } else if (trackingMode === 'helicopter') {
            // 시네마 모드가 쓰는 모드다. 15 는 열차가 너무 작아 볼 것이 없었다.
            zoom = 16;
            bearing = trackingParams.bearing.fn(now);
            pitch = 60;
        } else if (trackingMode === 'drone') {
            zoom = 17;
            bearing = trackingParams.bearing.fn(now);
            pitch = 75;
        } else if (trackingMode === 'bird') {
            me.updateTrackingParams();
            zoom = trackingParams.zoom.fn(now);
            bearing = trackingParams.bearing.fn(now);
            pitch = trackingParams.pitch.fn(now);
        } else {
            if (trackingMode === 'front' || trackingMode === 'topfront') {
                bearing = (bearing + 360) % 360 - 180;
            }
            // 사용자가 돌린 만큼을 목표각에 먼저 더한다. 아래 이징 뒤에 더하면
            // 이징이 매 프레임 오프셋을 되돌려 회전이 먹지 않는다.
            if (cameraOverride && cameraOverride.bearingOffset !== undefined) {
                bearing += cameraOverride.bearingOffset;
            }
            if (bearingFactor >= 0) {
                bearingFactor = 1 - Math.pow(1 - bearingFactor, frameRateFactor);
                bearing = currentBearing + wrapAngle(bearing - currentBearing) * bearingFactor;
            }
            if (trackingMode === 'back' || trackingMode === 'front') {
                zoom = 18.5;
                pitch = 85;
            } else {
                zoom = 15;
                pitch = 60;
            }
        }

        // 사용자가 카메라를 잡은 뒤로는 pitch 와 zoom 도 사용자 것이다. zoom 을 넘기는
        // 이유는 touchZoomRotate 가 핀치줌과 회전을 한 핸들러로 묶어서다 — 회전만 열면
        // 모바일에서 핀치할 때마다 모드가 zoom 을 되돌린다.
        if (cameraOverride) {
            zoom = me.baseZoom;
            pitch = cameraOverride.pitch;
            if (cameraOverride.bearing !== undefined) {
                bearing = cameraOverride.bearing;
            }
        }

        const cameraToGround = getCameraMercatorZ(map) / Math.cos(currentPitch * DEGREE_TO_RADIAN),
            cameraToObject = cameraToGround * Math.pow(2, currentZoom - zoom),
            objectToGround = me.getModelPosition(center, altitude).z / Math.cos(pitch * DEGREE_TO_RADIAN);

        // Adjust zoom according to the altitude of the object
        zoom = Math.min(zoom - Math.log2(Math.max(cameraToObject + objectToGround, 0) / cameraToObject), 22);

        center = me.adjustCoord(center, altitude, bearing);
        if (easeOutFactor >= 0) {
            const currentCenter = map.getCenter();

            easeOutFactor = 1 - Math.pow(1 - easeOutFactor, frameRateFactor);
            center = new LngLat(
                helpers.lerp(currentCenter.lng, center.lng, easeOutFactor),
                helpers.lerp(currentCenter.lat, center.lat, easeOutFactor)
            );
        }
        if (easeInFactor >= 0) {
            easeInFactor = 1 - Math.pow(1 - easeInFactor, frameRateFactor);
            zoom = helpers.lerp(currentZoom, zoom, easeInFactor);
            pitch = helpers.lerp(currentPitch, pitch, easeInFactor);
        }

        // Prevent layer switch due to calculation error
        if (Math.floor(zoom + 1) - zoom < 1e-6) {
            zoom = Math.floor(zoom + 1);
        }

        // Prevent layer switch back and forth
        if (me.prevLayerZoom === getLayerZoom(zoom)) {
            zoom = currentZoom;
        } else {
            delete me.prevLayerZoom;
        }

        // jumpTo 는 안에서 stop() 을 부르고, 그게 handlers.stop(false) 로 모든 입력
        // 핸들러를 reset 한다. 추적 중에는 이 함수가 매 프레임 도니까 사용자가 끌고
        // 있는 회전·pitch 드래그가 프레임마다 초기화돼 각도가 하나도 안 쌓인다.
        // _updatingCamera 는 mapbox 가 "핸들러 처리에서 나온 카메라 갱신은 리셋하지
        // 않는다"고 쓰는 자신의 가드다(HandlerManager#stop 첫 줄). 프레임 추적 갱신도
        // 같은 성격이라 그 가드를 빌린다.
        // ponytail: 비공개 필드다. mapbox-gl 을 올릴 때 HandlerManager#stop 의 가드가
        // 그대로인지 확인한다 — 없어지면 추적 중 회전이 다시 안 먹는다.
        const handlerManager = map.handlers,
            wasUpdatingCamera = handlerManager && handlerManager._updatingCamera;

        if (handlerManager) {
            handlerManager._updatingCamera = true;
        }
        map.jumpTo({center, zoom, bearing, pitch}, {tracking: true});
        if (handlerManager) {
            handlerManager._updatingCamera = wasUpdatingCamera;
        }

        // updateCameraOverride 가 "사용자가 돌린 양"만 추려내는 기준점. 우리가 방금 쓴
        // 값과의 차이가 곧 사용자 입력이므로 이징 지연이 오프셋에 섞이지 않는다.
        // bearing 이 아니라 getBearing() 을 쓴다: 오프셋을 더한 bearing 은 ±180 을
        // 넘을 수 있고, 그러면 델타 계산의 (diff + 540) 이 음수가 되어 JS 의 음수 %
        // 때문에 각도가 튄다. 지도가 정규화한 값을 기준으로 삼는다.
        me.lastAppliedBearing = map.getBearing();

        // Workaround for the issue of the scroll zoom during tracking
        if (scrollZooming) {
            map.scrollZoom._active = true;
        }
    }

    refreshTrains() {
        const me = this,
            initialSelection = me.initialSelection,
            now = me.clock.getTimeOffset();

        for (const timetable of me.timetables.getAll()) {
            if (timetable.start <= now && now <= timetable.end && !me.standbyTrainLookup.has(timetable.id)) {
                me.trainStart(new Train(timetable));
            }
        }

        me.trafficLayer.refreshDelayMarkers();

        me.trainLoaded = true;
        if (initialSelection) {
            me.setSelection(initialSelection);
        }
    }

    trainStart(train, options = {}) {
        const me = this,
            railway = train.r;

        if (me.hiddenRailways.has(railway.id) || me.checkActiveTrains(train) || (railway.status && railway.dynamic && !me.realtimeTrains.has(train.id)) || railway.suspended) {
            return;
        }
        if (!me.setSectionData(train, options.index)) {
            return; // Out of range
        }

        me.activeTrainLookup.set(train.id, train);
        me.trafficLayer.addObject(train);
        me.trainRepeat(train, options.index);

        if (options.marked) {
            me.markObject(train);
        }
        if (options.tracked) {
            me.trackObject(train);
        }
        if (train.ad) {
            me.showAdTrainPopup(train);
        }
    }

    trainRepeat(train, index) {
        const me = this,
            {activeTrainLookup, clock} = me,
            timetable = train.timetable,
            now = clock.getTimeOffset(),
            minStandingDuration = configs.minStandingDuration;
        let final;

        if (timetable) {
            final = !me.setSectionData(train, index);
            if (final) {
                const nextTimetables = timetable.nt;

                if (nextTimetables) {
                    let needToStand = false;

                    for (const {t: id} of nextTimetables[0].pt) {
                        const prevTrain = activeTrainLookup.get(id);

                        if (prevTrain && prevTrain.arrivalStation) {
                            needToStand = true;
                        }
                    }
                    if (!needToStand) {
                        let marked = false,
                            tracked = false;

                        for (const {t: id} of nextTimetables[0].pt) {
                            const prevTrain = activeTrainLookup.get(id);

                            if (prevTrain) {
                                if (me.markedObject === prevTrain) {
                                    marked = true;
                                }
                                if (me.trackedObject === prevTrain) {
                                    tracked = true;
                                }
                                me.stopTrain(prevTrain);
                            }
                        }
                        nextTimetables.forEach((nextTimetable, i) => {
                            const nextTrain = me.standbyTrainLookup.get(nextTimetable.id) || new Train(nextTimetable);

                            me.trainStart(nextTrain, i === 0 ? {index: 0, marked, tracked} : {index: 0});
                        });
                    }
                    return;
                }
            }
        } else {
            final = !me.setSectionData(train, undefined, !me.realtimeTrains.has(train.id));
        }

        if (me.markedObject === train) {
            train.standing = true;
        }
        // ⚠️ **말풍선은 `markedObject`(마우스 오버) 뿐 아니라 `trackedObject`(클릭)
        // 일 때도 다시 그려야 한다.** 클릭해서 띄운 말풍선은 마우스가 떠난 뒤에도
        // 남는데, 조건이 markedObject 뿐이면 그 뒤로 HTML 이 한 번도 갱신되지 않아
        // 다음 역·도착 정보가 선택한 순간에 얼어붙는다. getPopupObject() 가 둘을
        // 합쳐서 돌려주므로 그걸 쓴다 (updatePopup 도 같은 함수로 대상을 고른다).
        if (me.getPopupObject() === train) {
            me.updatePopup({setHTML: true});
        }

        const sectionLength = train.sectionLength;

        if (!final && sectionLength !== 0) {
            const delay = train.delay || 0,
                minDelay = configs.minDelay,
                {departureTime, arrivalTime, nextDepartureTime, sectionIndex} = train,
                feature = me.featureLookup.get(`${train.r.id}.18`),
                stationOffsets = feature.properties['station-offsets'],
                distance = Math.abs(stationOffsets[sectionIndex + sectionLength] - stationOffsets[sectionIndex]),
                actualDepartureTime = index !== undefined ? Math.max(departureTime + delay, now + (clock.speed === 1 ? minStandingDuration : 0)) : departureTime !== undefined ? departureTime + delay : now;
            let {maxSpeed, acceleration, maxAccelerationTime, maxAccDistance} = configs,
                duration, minDuration, maxDuration, accelerationTime;

            if (maxDuration === undefined && nextDepartureTime !== undefined) {
                maxDuration = nextDepartureTime - minStandingDuration + 60000 + delay - minDelay - actualDepartureTime;
            }
            if (arrivalTime !== undefined) {
                minDuration = arrivalTime + delay - minDelay - actualDepartureTime;
                if (!(maxDuration < minDuration + 60000)) {
                    maxDuration = minDuration + 60000;
                }
            }

            if (distance <= maxAccDistance * 2) {
                duration = Math.sqrt(distance / acceleration) * 2;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, minDuration || 0, maxDuration);
                    acceleration = distance * 4 / duration / duration;
                }
                accelerationTime = duration / 2;
            } else {
                duration = maxAccelerationTime * 2 + (distance - maxAccDistance * 2) / maxSpeed;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, minDuration || 0, maxDuration);
                    maxAccDistance = acceleration * duration * duration / 8;
                    if (distance >= maxAccDistance * 2) {
                        maxSpeed = distance * 2 / duration;
                        acceleration = maxSpeed * 2 / duration;
                    } else {
                        maxSpeed = acceleration * duration / 2 - Math.sqrt(acceleration * (maxAccDistance * 2 - distance));
                    }
                }
                accelerationTime = maxSpeed / acceleration;
            }
            me.trafficLayer.updateObject(train, actualDepartureTime, duration, accelerationTime, acceleration / distance, accelerationTime, acceleration / distance);

            train.animationID = animation.start({
                complete: () => {
                    me.trainRepeat(train, timetable ? train.timetableIndex + 1 : undefined);
                },
                duration: actualDepartureTime + duration - now,
                clock
            });
        } else {
            train.animationID = animation.start({
                complete: () => {
                    if (final) {
                        me.stopTrain(train);
                    } else {
                        me.trainRepeat(train);
                    }
                },
                duration: timetable ?
                    Math.max(train.departureTime - now, minStandingDuration) :
                    final ? minStandingDuration : configs.realtimeCheckInterval,
                clock
            });
        }
    }

    refreshFlights() {
        const me = this,
            initialSelection = me.initialSelection,
            now = me.clock.getTimeOffset();

        for (const flight of me.flightLookup.values()) {
            if (flight.entry <= now && now <= flight.end) {
                me.flightStart(flight);
            }
        }

        me.flightLoaded = true;
        if (initialSelection) {
            me.setSelection(initialSelection);
        }
    }

    flightStart(flight) {
        const me = this,
            {activeFlightLookup, clock} = me,
            id = flight.id;

        if (activeFlightLookup.has(id)) {
            return;
        }

        activeFlightLookup.set(id, flight);
        me.trafficLayer.addObject(flight);

        const now = clock.getTimeOffset(),
            distance = flight.feature.properties.length,
            maxSpeed = flight.maxSpeed,
            acceleration = flight.acceleration > 0 ? flight.acceleration : 0,
            deceleration = flight.acceleration > 0 ? 0 : -flight.acceleration,
            accelerationTime = acceleration > 0 ? maxSpeed / acceleration : 0,
            decelerationTime = deceleration > 0 ? maxSpeed / deceleration : 0,
            duration = accelerationTime / 2 + distance / maxSpeed + decelerationTime / 2;

        me.trafficLayer.updateObject(flight, flight.start, duration, accelerationTime, acceleration / distance, decelerationTime, deceleration / distance);

        flight.animationID = animation.start({
            complete: () => {
                me.stopFlight(flight);
            },
            duration: flight.end - now,
            clock
        });
    }

    refreshBuses(gtfsId) {
        const me = this,
            now = me.clock.getTimeOffset();

        for (const gtfs of gtfsId ? [me.gtfs.get(gtfsId)] : me.gtfs.values()) {
            // **합성 시각표로는 차량을 만들지 않는다.** 서울·경기 버스에는 공표
            // 다이어가 없어서 stop_times 가 배차간격을 펼친 합성값이다. 그걸로
            // 스폰하면 실시간에 없는 차량이 화면에 뜬다 - 정적 노선은 첫차 시각부터
            // 편도 소요시간 동안 창이 열려 있어 05시에 271개 트립이 동시에 걸렸다.
            //
            // 실시간 피드가 본체다: refreshRealtimeBusData 가 VehiclePosition 마다
            // 차량을 직접 만들고(map.js 아래 loadDynamicBusData 분기) 여기에
            // 의존하지 않는다. 그래서 이 루프를 건너뛰어도 실제 차량은 그대로 뜬다.
            //
            // 트립은 여전히 필요하다 - 노선선·정류장·tripId 슬롯의 그릇이다.
            if (gtfs.frequencyBased) {
                continue;
            }
            for (const trip of gtfs.trips.getAll()) {
                const departureTimes = trip.departureTimes,
                    route = gtfs.routes.get(trip.route);

                if (departureTimes[0] <= now && now <= departureTimes[departureTimes.length - 1] && !gtfs.activeBusLookup.has(trip.id) && route && !route.hidden) {
                    let offset = 0;
                    const feature = gtfs.featureLookup.get(trip.shape);

                    if (!feature) {
                        continue;
                    }

                    const offsets = trip.stops.map(stopId =>
                        // Use the previous offset to calulate a weight and pick a closer point
                        (offset = nearestCloserPointOnLine(feature, gtfs.stops.get(stopId).coord, offset).properties.totalDistance)
                    );

                    me.busStart(new Bus({
                        gtfsId: gtfs.id,
                        trip,
                        feature,
                        offsets,
                        offset: 0
                    }));
                }
            }
        }
    }

    busStart(bus) {
        const me = this;

        if (bus.anchor) {
            // 실시간 버스는 궤적으로 굴린다 (helpers/trajectory.js).
            bus.sectionIndex = 0;
            bus.sectionLength = 1;
            me.gtfs.get(bus.gtfsId).activeBusLookup.set(bus.trip.id, bus);
            me.trafficLayer.addObject(bus);
            me.busTrack(bus);
            return;
        }
        if (!me.setBusSectionData(bus)) {
            return;
        }
        me.gtfs.get(bus.gtfsId).activeBusLookup.set(bus.trip.id, bus);
        me.trafficLayer.addObject(bus);
        me.busRepeat(bus);
    }

    /**
     * 실시간 버스를 **시간의 함수**로 그린다 (helpers/trajectory.js 참조).
     *
     * 한 틱(`realtimeCheckInterval`) 앞을 내다보고 그 사이를 등속으로 잇는다.
     * 다음 틱에 다시 계획하므로 궤적을 따라가고, 관측이 오면 닻이 바뀌어
     * 블렌드가 그 차이를 흡수한다. **위상·접근감속·구간수 상한이 필요 없다** -
     * 어긋남을 녹이는 자리가 여기 한 곳이기 때문이다.
     * @param {Object} bus - 실시간 버스
     */
    busTrack(bus) {
        const me = this,
            clock = me.clock,
            now = clock.getTime(),
            horizon = configs.realtimeCheckInterval,
            {offsets, trip, anchor, prevAnchor} = bus,
            times = trip.departureTimes,
            last = offsets[offsets.length - 1],
            s1 = positionAt(anchor, prevAnchor, offsets, times, now),
            s2 = positionAt(anchor, prevAnchor, offsets, times, now + horizon);

        animation.stop(bus.animationID);

        // 패널·팝업이 읽는 '다가가는 정류장'. 그리는 위치에서 유도한다.
        bus.stop = trip.stopSequences[offsets.reduce(
            (acc, cur, i) => cur < s1 ? Math.min(i + 1, offsets.length - 1) : acc, 0
        )];
        bus.offset = s1;

        // 피드에서 사라진 차량이 종점에 닿으면 끝낸다 (유령 방지).
        if (s1 >= last - 1 && !me.gtfs.get(bus.gtfsId).realtimeBuses.has(trip.id)) {
            me.stopBus(bus);
            return;
        }

        // 뒤로 갈 때 구간을 거꾸로 잡는 산술은 `trajectory.sectionFor` 에 있다
        // (부호가 두 번 뒤집히는 자리라 순수 함수로 두고 검사한다).
        const {index, length, f1, f2} = sectionFor(offsets, s1, s2);

        bus.sectionIndex = index;
        bus.sectionLength = length;

        // 모델이 멈춰 있다(종점·퇴화 구간). 자리만 잡고 다음 틱에 다시 본다.
        if (!(f2 > f1)) {
            me.trafficLayer.updateObject(bus, clock.getTimeOffset(), horizon, 0, 0, 0, 0);
            bus.animationID = animation.start({
                complete: () => me.busTrack(bus), duration: horizon, clock
            });
            return;
        }

        const plan = planSection(clock.getTimeOffset(), horizon, f1, f2);

        me.trafficLayer.updateObject(bus, plan.startTime, plan.duration,
            plan.accelerationTime, plan.acceleration,
            plan.accelerationTime, plan.acceleration);
        bus.animationID = animation.start({
            complete: () => me.busTrack(bus), duration: horizon, clock
        });
    }

    /**
     * 시간표 버스의 구간 하나를 계획한다. **실시간 버스는 `busTrack` 이 굴린다.**
     * @param {Object} bus - 버스
     * @param {number} [index] - 갈 다음 구간
     */
    busRepeat(bus, index) {
        const me = this,
            clock = me.clock,
            now = clock.getTimeOffset(),
            minBusStandingDuration = configs.minBusStandingDuration,
            final = !me.setBusSectionData(bus, index);

        // 열차와 같은 이유로 getPopupObject() 를 쓴다 (위 trainRepeat 주석 참조).
        if (me.getPopupObject() === bus) {
            me.updatePopup({setHTML: true});
        }
        if (me.trackedObject === bus && me.detailPanel) {
            me.detailPanel.updateHeader();
        }

        const {offsets, sectionIndex, sectionLength} = bus,
            distance = Math.abs(offsets[sectionIndex + sectionLength] - offsets[sectionIndex]);

        // Sometimes distance becomes 0 because the busroute coordinates
        // are incorrect and a few busstops shares the same coordinates.
        if (!final && sectionLength > 0 && distance === 0) {
            console.log('bus zero interval:', bus);
        }

        if (!final && sectionLength > 0 && distance > 0) {
            // 실시간 버스는 시간표로 페이스가 잡히지 않는다 - nextDepartureTime 은
            // 이 경로에서 undefined 라 아래 maxDuration 클램프가 아예 안 걸리고,
            // 걸려도 상한이라 느리게 만들지 못한다. 그래서 configs 의 30km/h 로
            // 전부 달려 폴링 간격보다 먼저 도착해 멈춰 서 있다가 순간이동했다.
            //
            // 구간 소요는 시간표 델타로 잡는다. 관측 기반 피드면 이게 그 구간의
            // 실측 소요다 - 노선 하나에 속도 하나로 깔면 고속화도로 구간과 도심
            // 구간이 둘 다 틀린다 (9401 실측 구간 중위 11 vs 64km/h).
            // 시간표 버스는 시간표(`departureTime`/`nextDepartureTime`)가 페이스를
            // 잡는다. 실시간 버스는 여기로 오지 않는다 (`busTrack`).
            const sectionDuration = 0,
                minDelay = configs.minDelay,
                {departureTime, nextDepartureTime} = bus,
                actualDepartureTime = index !== undefined ? Math.max(departureTime, now + (clock.speed === 1 ? minBusStandingDuration : 0)) : helpers.valueOrDefault(departureTime, now);
            let {maxBusSpeed, busAcceleration, maxBusAccelerationTime, maxBusAccDistance} = configs,
                duration, maxDuration, accelerationTime;

            // 구간 평균속도를 상한으로 두면 아래 물리 계산이 항상 그보다 느려지고
            // (가감속 램프 때문에 duration = 소요 + v/a), 클램프가 정확히 소요로
            // 되돌리면서 가속도를 다시 푼다. 즉 구간 통과시간이 시간표와 일치한다.
            if (sectionDuration > 0) {
                maxDuration = sectionDuration;
                maxBusSpeed = distance / sectionDuration;
                maxBusAccelerationTime = maxBusSpeed / busAcceleration;
                maxBusAccDistance = maxBusAccelerationTime * maxBusSpeed / 2;
            }

            if (maxDuration === undefined && nextDepartureTime !== undefined) {
                maxDuration = nextDepartureTime - minBusStandingDuration + 60000 - minDelay - actualDepartureTime;
            }

            if (distance <= maxBusAccDistance * 2) {
                duration = Math.sqrt(distance / busAcceleration) * 2;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, 0, maxDuration);
                    busAcceleration = distance * 4 / duration / duration;
                }
                accelerationTime = duration / 2;
            } else {
                duration = maxBusAccelerationTime * 2 + (distance - maxBusAccDistance * 2) / maxBusSpeed;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, 0, maxDuration);
                    maxBusAccDistance = busAcceleration * duration * duration / 8;
                    if (distance >= maxBusAccDistance * 2) {
                        maxBusSpeed = distance * 2 / duration;
                        busAcceleration = maxBusSpeed * 2 / duration;
                    } else {
                        maxBusSpeed = busAcceleration * duration / 2 - Math.sqrt(busAcceleration * (maxBusAccDistance * 2 - distance));
                    }
                }
                accelerationTime = maxBusSpeed / busAcceleration;
            }
            // 관측이 가리키는 지점에서 출발시킨다. 이게 없으면 실시간 버스가 늘
            // 구간 시작점에서 출발해, 피드의 구간 안 위치가 화면에 닿지 않는다.
            const startTime = actualDepartureTime;

            me.trafficLayer.updateObject(bus, startTime, duration, accelerationTime, busAcceleration / distance, accelerationTime, busAcceleration / distance);

            bus.animationID = animation.start({
                complete: () => {
                    me.busRepeat(bus, bus.stop === undefined ? bus.sectionIndex + 1 : undefined);
                },
                duration: startTime + duration - now,
                clock
            });
        } else {
            bus.animationID = animation.start({
                complete: () => {
                    if (final) {
                        me.stopBus(bus);
                    } else {
                        me.busRepeat(bus);
                    }
                },
                duration: bus.stop === undefined ?
                    Math.max(bus.nextDepartureTime - now, minBusStandingDuration) :
                    final ? minBusStandingDuration : configs.realtimeCheckInterval,
                clock
            });
        }
    }

    updateTrackingParams(reset) {
        const me = this,
            {map, trackingMode, trackingParams} = me,
            now = performance.now();

        if (trackingMode === 'bird') {
            const {zoom, bearing, pitch} = trackingParams;

            if (!zoom.time || reset) {
                const time = zoom.time = [0, now, 0, 0],
                    value = zoom.value = [0, me.baseZoom, 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 30000);
                    value[i] = Math.random() * 5 + 15;
                }
                zoom.fn = createInterpolant(time, value);
            } else if (now >= zoom.time[2]) {
                zoom.time = zoom.time.slice(1).concat(zoom.time[3] + Math.random() * 10000 + 30000);
                zoom.value = zoom.value.slice(1).concat(Math.random() * 5 + 15);
                zoom.fn = createInterpolant(zoom.time, zoom.value);
            }
            if (!bearing.time || reset) {
                const time = bearing.time = [0, now, 0, 0],
                    value = bearing.value = [0, map.getBearing(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 40000);
                    value[i] = Math.random() * 360 - 180;
                }
                bearing.fn = createInterpolant(time, value);
            } else if (now >= bearing.time[2]) {
                bearing.time = bearing.time.slice(1).concat(bearing.time[3] + Math.random() * 10000 + 40000);
                bearing.value = bearing.value.slice(1).concat(Math.random() * 360 - 180);
                bearing.fn = createInterpolant(bearing.time, bearing.value);
            }
            if (!pitch.time || reset) {
                const time = pitch.time = [0, now, 0, 0],
                    value = pitch.value = [0, map.getPitch(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 20000);
                    value[i] = Math.random() * 30 + 45;
                }
                pitch.fn = createInterpolant(time, value);
            } else if (now >= pitch.time[2]) {
                pitch.time = pitch.time.slice(1).concat(pitch.time[3] + Math.random() * 10000 + 20000);
                pitch.value = pitch.value.slice(1).concat(Math.random() * 30 + 45);
                pitch.fn = createInterpolant(pitch.time, pitch.value);
            }
        } else {
            delete trackingParams.zoom.time;
            delete trackingParams.bearing.time;
            delete trackingParams.pitch.time;
            if (trackingMode === 'drone') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing - (t - now) / 200) % 360;
            } else if (trackingMode === 'helicopter') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing + (t - now) / 400) % 360;
            }
        }
    }

    updateHandlersAndControls() {
        const me = this,
            {map, trackedObject, trackingMode} = me,
            handlers = ['scrollZoom', 'boxZoom', 'dragRotate', 'dragPan', 'keyboard', 'doubleClickZoom', 'touchZoomRotate', 'touchPitch'];

        for (const handler of handlers) {
            map[handler].enable();
        }
        me.navControl.enable();

        if (me.cinemaMode) {
            // 알아서 돌아가는 화면이므로 줌은 사용자가 잡지 않는다. 거리는 시네마가
            // 정하고(helicopter zoom), 열차가 주기마다 바뀌므로 손으로 맞춰둔 줌은
            // 어차피 다음 열차에서 무의미하다.
            // touchZoomRotate 는 핀치줌과 회전을 한 핸들러로 묶고 있어 같이 막힌다 —
            // 모바일에서 시네마 중 손가락 회전도 안 된다.
            for (const handler of ['scrollZoom', 'boxZoom', 'doubleClickZoom', 'touchZoomRotate']) {
                map[handler].disable();
            }
            me.navControl.disable();
        }

        if (isVehicle(trackedObject)) {
            // 열차가 화면 중앙에 있어야 하므로 팬은 어느 모드에서도 막는다.
            map.dragPan.disable();

            if (trackingMode !== 'position') {
                // 자동 카메라가 도는 모드에서도 각도와 줌은 사용자가 잡을 수 있어야 한다
                // (_jumpTo 의 cameraOverride 가 그 값을 유지한다). 카메라를 통째로
                // 튀게 만드는 나머지 입력만 막는다.
                for (const handler of ['boxZoom', 'keyboard', 'doubleClickZoom']) {
                    map[handler].disable();
                }
            }
        }
    }

    startViewAnimation() {
        const me = this;
        let t2 = 0,
            t4 = 0;

        me.viewAnimationID = animation.start({
            callback: (elapsed, duration) => {
                const target = me.getTrackedCameraTarget(),
                    t1 = easeOutQuart(elapsed / duration),
                    easeOutFactor = 1 - (1 - t1) / (1 - t2),
                    t3 = easeInQuad(elapsed / duration),
                    easeInFactor = 1 - (1 - t3) / (1 - t4);

                me._jumpTo({
                    center: target.coord,
                    altitude: target.altitude,
                    bearing: target.bearing,
                    easeOutFactor,
                    easeInFactor,
                    bearingFactor: easeOutFactor
                });
                t2 = t1;
                t4 = t3;
            },
            complete: () => {
                delete me.viewAnimationID;
            },
            duration: 1000
        });
    }

    stopViewAnimation() {
        const me = this;

        if (me.viewAnimationID) {
            animation.stop(me.viewAnimationID);
            delete me.viewAnimationID;
        }
    }

    /**
     * Returns a LngLat representing geographical coordinates on the ground
     * that shares the same projected pixel coordinates with the specified
     * geographical location and altitude.
     * @param {LngLatLike} coord - The geographical location to adjust
     * @param {number} altitude - The altitude in meters of the position
     * @param {number} bearing - The desired bearing of the camera in degrees.
     *     If not specified, the coordinates will be calculated using the
     *     current camera projection matrix
     * @returns {LngLat} The adjusted LngLat.
     */
    adjustCoord(coord, altitude, bearing) {
        if (!altitude) {
            return LngLat.convert(coord);
        }

        const {map, trafficLayer} = this;

        if (!isNaN(bearing)) {
            const mCoord = MercatorCoordinate.fromLngLat(coord, altitude),
                offset = mCoord.z * Math.tan(map.getPitch() * DEGREE_TO_RADIAN),
                x = mCoord.x + offset * Math.sin(bearing * DEGREE_TO_RADIAN),
                y = mCoord.y - offset * Math.cos(bearing * DEGREE_TO_RADIAN);

            return new MercatorCoordinate(x, y, 0).toLngLat();
        }
        return map.unproject(trafficLayer.project(coord, altitude));
    }

    getLocalizedRailwayTitle(railway) {
        const me = this,
            title = (railway || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedTrainNameOrRailwayTitle(names, railway) {
        const me = this;

        return names ?
            names.map(name => name[me.lang] || name.en).join(me.dict['and']) :
            me.getLocalizedRailwayTitle(railway);
    }

    getLocalizedTrainTypeTitle(type) {
        const me = this,
            title = (type || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedStationTitle(array) {
        const me = this,
            stations = Array.isArray(array) ? array : [array];

        return stations.map(station => {
            const title = (station || {}).title || {};
            return title[me.lang] || title.en;
        }).join(me.dict['and']);
    }

    getLocalizedRailDirectionTitle(direction) {
        const me = this,
            title = (direction || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedDestinationTitle(destination, direction) {
        const me = this;

        return destination ?
            me.dict['for'].replace('$1', me.getLocalizedStationTitle(destination)) :
            me.getLocalizedRailDirectionTitle(direction);
    }

    getLocalizedOperatorTitle(operator) {
        const me = this,
            title = (operator || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedAirportTitle(airport) {
        const me = this,
            title = (airport || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedFlightStatusTitle(status) {
        const me = this,
            title = (status || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOITitle(poi) {
        const me = this,
            title = (poi || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOIDescription(poi) {
        const me = this,
            description = (poi || {}).description || {};

        return description[me.lang] || description.en;
    }

    getTrainDescription(train) {
        const me = this,
            {lang, dict} = me,
            {r: railway, ad, departureTime, arrivalStation} = train,
            color = (ad && ad.color) || (train.v || railway).color,
            delay = train.delay || 0,
            arrivalTime = helpers.valueOrDefault(train.arrivalTime, train.nextDepartureTime),
            congestion = me.getCongestion(train),
            status = railway.status;

        return [
            '<div class="desc-header">',
            Array.isArray(color) ? [
                '<div>',
                ...color.slice(0, 3).map(c => `<div class="line-strip" style="background-color: ${c};"></div>`),
                '</div>'
            ].join('') : `<div style="background-color: ${color};"></div>`,
            '<div><strong>',
            me.getLocalizedTrainNameOrRailwayTitle(train.nm, railway),
            '</strong>',
            `<br> <span class="train-type-label">${me.getLocalizedTrainTypeTitle(train.y)}</span> `,
            me.getLocalizedDestinationTitle(train.ds, train.d),
            '</div></div>',
            `<strong>${dict['train-number']}:</strong> ${train.n}`,
            train.carNumber ? ` (${dict['car-number'].replace('$1', train.carNumber)}${train.carComposition ? `/${train.carComposition}` : ''})` : '',
            !train.timetable ? ` <span class="desc-caution">${dict['special']}</span>` : '',
            congestion !== undefined ?
                `<br><strong>${dict['congestion']}:</strong> ${Math.round(congestion)}% <span class="desc-small">${dict['congestion-note']}</span>` : '',
            '<br>',
            ad ? `<span class="desc-ad-cars" style="color: ${ad.textcolor};">${ad.description[lang]}</span><br>` : '',
            delay >= configs.delayThreshold ? '<span class="desc-caution">' : '',
            '<strong>',
            dict[train.standing ? 'standing-at' : 'previous-stop'],
            ':</strong> ',
            me.getLocalizedStationTitle(train.departureStation),
            departureTime !== undefined ? ` ${helpers.getTimeString(departureTime + delay)}` : '',
            arrivalStation ? [
                `<br><strong>${dict['next-stop']}:</strong> `,
                me.getLocalizedStationTitle(arrivalStation),
                arrivalTime !== undefined ? ` ${helpers.getTimeString(arrivalTime + delay)}` : ''
            ].join('') : '',
            delay >= configs.delayThreshold ? `<br>${dict['delay'].replace('$1', Math.floor(delay / 60000))}</span>` : '',
            status && lang === 'ja' ? `<br><span class="desc-caution"><strong>${status}:</strong> ${railway.text}</span>` : ''
        ].join('');
    }

    getFlightDescription(flight) {
        const me = this,
            dict = me.dict,
            {a: airline, n: flightNumber, ds: destination} = flight,
            tailcolor = airline.tailcolor || '#FFFFFF',
            scheduledTime = helpers.valueOrDefault(flight.sdt, flight.sat),
            estimatedTime = helpers.valueOrDefault(flight.edt, flight.eat),
            actualTime = helpers.valueOrDefault(flight.adt, flight.aat),
            delayed = (estimatedTime !== undefined || actualTime !== undefined) && scheduledTime !== helpers.valueOrDefault(estimatedTime, actualTime);

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${tailcolor};"></div>`,
            `<div><strong>${me.getLocalizedOperatorTitle(airline)}</strong>`,
            `<br>${flightNumber[0]} `,
            dict[destination ? 'to' : 'from'].replace('$1', me.getLocalizedAirportTitle(destination || flight.or)),
            '</div></div>',
            `<strong>${dict['status']}:</strong> ${me.getLocalizedFlightStatusTitle(flight.s)}`,
            '<br><strong>',
            dict[destination ? 'scheduled-departure-time' : 'scheduled-arrival-time'],
            `:</strong> ${helpers.getTimeString(scheduledTime)}`,
            delayed ? '<span class="desc-caution">' : '',
            estimatedTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'estimated-departure-time' : 'estimated-arrival-time'],
                `:</strong> ${helpers.getTimeString(estimatedTime)}`
            ].join('') : actualTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'actual-departure-time' : 'actual-arrival-time'],
                `:</strong> ${helpers.getTimeString(actualTime)}`
            ].join('') : '',
            delayed ? '</span>' : '',
            flightNumber.length > 1 ? `<br><strong>${dict['code-share']}:</strong> ${flightNumber.slice(1).join(' ')}` : ''
        ].join('');
    }

    getBusDescription(bus) {
        const me = this,
            dict = me.dict,
            gtfs = me.gtfs.get(bus.gtfsId),
            stops = gtfs.stops,
            {route, headsigns, stops: stopIds} = bus.trip,
            {shortName, longName, color, textColor} = gtfs.routes.get(route),
            labelStyle = [
                textColor ? `color: ${textColor};` : '',
                color ? `background-color: ${color};` : ''
            ].join(' '),
            nextStopIndex = bus.sectionIndex + (bus.standing ? 0 : bus.sectionLength),
            nextStopName = stops.get(stopIds[nextStopIndex]).name,
            prevStopIndex = Math.max(0, nextStopIndex - 1),
            prevStopName = stops.get(stopIds[prevStopIndex]).name;

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${gtfs.color};"></div>`,
            `<div><strong>${gtfs.agency}</strong>`,
            bus.stop !== undefined ? '<span class="realtime-small-icon"></span>' : '',
            '<br>',
            shortName || longName ? ` <span class="bus-route-label" style="${labelStyle}">${shortName || longName}</span> ` : '',
            headsigns.length === 0 ? longName : headsigns[headsigns.length === 1 ? 0 : prevStopIndex],
            '</div></div>',
            bus.id ? `<strong>${dict['vehicle-number']}:</strong> ${bus.id}<br>` : '',
            `<strong>${dict['previous-busstop']}:</strong> ${prevStopName}<br>`,
            `<strong>${dict['next-busstop']}:</strong> ${nextStopName}`
        ].join('');
    }

    /**
     * Check if any of connecting trains is active
     * @param {Object} train - train to check
     * @returns {boolean} True if any of connecting trains is active
     */
    checkActiveTrains(train) {
        const me = this;

        function check(curr, prop) {
            const activeTrain = me.activeTrainLookup.get(curr.t);

            // Need to check timetable ID
            if (activeTrain && curr.id === activeTrain.timetable.id) {
                return true;
            }

            const timetables = curr[prop];

            if (timetables) {
                for (const timetable of timetables) {
                    if (check(timetable, prop)) {
                        return true;
                    }
                }
            }
            return false;
        }

        return train.timetable ? check(train.timetable, 'pt') || check(train.timetable, 'nt') : false;
    }

    stopTrain(train) {
        const me = this;

        animation.stop(train.animationID);
        me.hideAdTrainPopup(train);
        if (train === me.markedObject) {
            me.markObject();
        }
        if (train === me.trackedObject) {
            me.trackObject();
        }
        me.activeTrainLookup.delete(train.id);
        return me.trafficLayer.removeObject(train);
    }

    stopFlight(flight) {
        const me = this;

        animation.stop(flight.animationID);
        if (flight === me.markedObject) {
            me.markObject();
        }
        if (flight === me.trackedObject) {
            me.trackObject();
        }
        me.activeFlightLookup.delete(flight.id);
        return me.trafficLayer.removeObject(flight);
    }

    stopBus(bus) {
        const me = this;

        animation.stop(bus.animationID);
        if (bus === me.markedObject) {
            me.markObject();
        }
        if (bus === me.trackedObject) {
            me.trackObject();
        }
        me.gtfs.get(bus.gtfsId).activeBusLookup.delete(bus.trip.id);
        return me.trafficLayer.removeObject(bus);
    }

    /**
     * Returns how full a train is where it is right now, as a percentage of its
     * capacity: 34% is every seat taken, 100% is the official capacity of 160 riders
     * per car. Only Seoul Metro lines 1-8 publish this, and only as a 30-minute
     * average per station and direction, so every car of a train shares one figure.
     * @param {Object} train - Train to look up
     * @returns {number} Congestion percentage, or undefined when unpublished
     */
    getCongestion(train) {
        const me = this,
            congestion = me.congestion,
            station = train.departureStation,
            direction = train.d;

        if (!congestion || !station || !direction) {
            return;
        }

        const line = (station.railway.id.match(/^Seoul\.Line(\d+)/) || [])[1],
            code = station.id.split('.').pop();

        if (!line) {
            return;
        }

        const rows = congestion.stations[`${line}.${code}`];

        if (!rows) {
            return;
        }

        const values = (rows[me.clock.getCalendar()] || {})[direction.id];

        if (!values) {
            return;
        }

        // Time offsets count from 3am, and the first slot is 05:30-06:00
        const index = Math.floor(me.clock.getTimeOffset() / 1800000) - 5,
            value = index >= 0 ? values[index] : undefined;

        return value === null ? undefined : value;
    }

    /**
     * Returns which congestion band a figure falls in.
     * @param {number} congestion - Congestion percentage
     * @returns {number} Index into CONGESTION_BANDS
     */
    getCongestionBandIndex(congestion) {
        return CONGESTION_BANDS.findIndex(({below}) => congestion < below);
    }

    /**
     * Returns the colour for a congestion figure.
     * @param {number} congestion - Congestion percentage
     * @returns {string} Colour
     */
    getCongestionColor(congestion) {
        return CONGESTION_BANDS[this.getCongestionBandIndex(congestion)].color;
    }

    /**
     * Paints the congestion colour on a roof triangle of the focused train, the way
     * buses carry occupancy on theirs, so the body keeps its line colour. Ordinary
     * trains keep their single triangle and it changes colour; express trains, whose
     * front triangle is already red for express, take the rear one instead. Only the
     * focused train is marked: the
     * figure is a 30-minute average per station and direction, which is worth reading
     * for the train you picked and is noise across the whole map.
     *
     * A train with no published figure is left alone - its triangle stays line-coloured
     * (roof white for the rear one), so "the data does not cover this train" stays
     * visibly different from "this train is empty".
     */
    refreshCongestionColors() {
        const me = this,
            {trafficLayer, congestionMode, trackedObject} = me,
            tinted = me.congestionTintedTrain,
            train = congestionMode && trackedObject && trackedObject.type === 'train' ?
                trackedObject : undefined,
            congestion = train ? me.getCongestion(train) : undefined;

        // 덮을 대상이 바뀌었거나 없어졌으면 먼저 벗긴다. 오버레이 슬롯이 하나라서
        // 안 벗기면 이전 열차가 새 열차의 색을 그대로 뒤집어쓴다. 되돌리는 자리는
        // 하나만 둔다 — 둘로 나눴다가 tinted 와 train 이 둘 다 undefined 인 기본
        // 상태에서 undefined 를 벗기려 들어 매 틱 던졌다.
        if (tinted && (tinted !== train || congestion === undefined)) {
            trafficLayer.setObjectTint(tinted);
            delete me.congestionTintedTrain;
        }
        if (congestion === undefined) {
            return;
        }

        // 급행 판정은 차종 이름이 아니라 색으로 한다 - 앞 삼각형(억센트, [3])이 노선색([2])과
        // 다르면 그 자리는 이미 무언가를 말하고 있으므로 뒤 삼각형을 쓴다. 차종 id 규칙
        // ('-Express')에 기대면 급행 표식을 다른 이름으로 추가할 때 조용히 덮어쓴다.
        const {v} = train,
            frontTaken = Array.isArray(v && v.color) && v.color[3] !== v.color[2];

        // 원본이 30분 평균을 역·방향 단위로만 주므로 한 편성의 모든 량이 한 값을
        // 공유한다 - 모든 량의 삼각형이 같은 색이 된다. 량별 데이터가 생기면
        // 량 단위로 덮으면 된다.
        trafficLayer.setObjectTint(train, me.getCongestionColor(congestion),
            frontTaken ? CONGESTION_TINT_ALPHA_EXPRESS : CONGESTION_TINT_ALPHA);
        me.congestionTintedTrain = train;
    }

    /**
     * Returns true if the feature belongs only to hidden railways.
     * @param {Object} properties - Feature properties
     * @returns {boolean} True if the feature must not be drawn
     */
    _isFeatureHidden(properties) {
        const hiddenRailways = this.hiddenRailways,
            {railway, railways} = properties;

        if (railway) {
            return hiddenRailways.has(railway);
        }
        if (railways) {
            return railways.slice(1, -1).split('|').every(id => hiddenRailways.has(id));
        }
        return false;
    }

    /**
     * Returns the Mapbox filter for a ground layer, which drops the features of
     * hidden railways.
     * @param {string} key - Layer key ('railways', 'stations' or 'stations-outline')
     * @param {number} zoom - Layer zoom level
     * @returns {Array} Mapbox filter expression
     */
    _getGroundFilter(key, zoom) {
        const me = this,
            isRailway = key === 'railways',
            visibleIDs = [...me.railways.getAll()].map(({id}) => id).filter(id => !me.hiddenRailways.has(id));

        return [
            'all',
            ['==', ['get', 'zoom'], zoom],
            ['==', ['get', 'type'], isRailway ? 0 : 1],
            isRailway ?
                ['!', ['in', ['get', 'railway'], ['literal', [...me.hiddenRailways]]]] :
                ['any', ...visibleIDs.map(id => ['in', `|${id}|`, ['get', 'railways']])]
        ];
    }

    _refreshRailwayLayers() {
        const me = this,
            {map, featureCollection} = me;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            for (const key of ['railways', 'stations', 'stations-outline']) {
                map.setFilter(`${key}-og-${zoom}`, me._getGroundFilter(key, zoom));
            }
            for (const key of ['railways', 'stations']) {
                const type = key === 'railways' ? 0 : 1;

                helpersMapbox.setLayerProps(map, `${key}-ug-${zoom}`, {
                    data: helpersGeojson.featureFilter(featureCollection, p =>
                        p.zoom === zoom && p.type === type && p.altitude < 0 && !me._isFeatureHidden(p)
                    )
                });
            }
        }
    }

    /**
     * Returns true if the railway runs trains on the map.
     * @param {string} id - Railway ID
     * @returns {boolean} True if the railway is visible
     */
    isRailwayVisible(id) {
        return !this.hiddenRailways.has(id);
    }

    /**
     * 노선 필터: 그 GTFS 노선이 보이는가.
     * @param {string} gtfsId - GTFS 피드 ID
     * @param {string} routeId - 노선 ID
     * @returns {boolean} 보이면 true
     */
    isBusRouteVisible(gtfsId, routeId) {
        const gtfs = this.gtfs.get(gtfsId),
            route = gtfs && gtfs.routes.get(routeId);

        return !!route && !route.hidden;
    }

    /**
     * GTFS 노선을 켜고 끈다. **선과 차량이 같이 따라간다** -
     * updateBusRouteVisibility 가 노선선 feature-state 를 다시 칠하고, 꺼진
     * 노선에서 달리던 버스를 멈춘다. 다시 켜면 lastRefresh 를 지우므로 다음
     * 틱에 새로 스폰된다 (열차의 setRailwayVisibility 와 같은 방식).
     * @param {string} gtfsId - GTFS 피드 ID
     * @param {string | Array<string>} routeIds - 노선 ID, 또는 그 목록
     * @param {boolean} visible - 보이게 하려면 true
     */
    setBusRouteVisibility(gtfsId, routeIds, visible) {
        const me = this,
            gtfs = me.gtfs.get(gtfsId);

        if (!gtfs) {
            return;
        }
        for (const id of Array.isArray(routeIds) ? routeIds : [routeIds]) {
            const route = gtfs.routes.get(id);

            if (route) {
                route.hidden = !visible;
            }
        }
        me.updateBusRouteVisibility();
    }

    /**
     * Shows or hides railways, with their trains and tracks.
     * @param {string | Array<string>} ids - Railway ID, or a list of them
     * @param {boolean} visible - True to show the railways
     */
    setRailwayVisibility(ids, visible) {
        const me = this,
            {hiddenRailways} = me,
            idSet = new Set(Array.isArray(ids) ? ids : [ids]);

        for (const id of idSet) {
            if (visible) {
                hiddenRailways.delete(id);
            } else {
                hiddenRailways.add(id);
            }
        }
        if (visible) {
            // ponytail: hidden trains are simply not started, so re-showing waits for
            // the next refreshTrains() tick to pick them up again.
            if (me.trainLoaded) {
                me.refreshTrains();
            }
        } else {
            for (const train of Array.from(me.activeTrainLookup.values())) {
                if (idSet.has(train.r.id)) {
                    me.stopTrain(train);
                }
            }
        }
        me._refreshRailwayLayers();
    }

    stopAll() {
        const me = this,
            promises = [];

        for (const train of me.activeTrainLookup.values()) {
            promises.push(me.stopTrain(train));
        }
        me.standbyTrainLookup.clear();
        me.realtimeTrains.clear();
        me.adTrains.clear();
        for (const flight of me.activeFlightLookup.values()) {
            promises.push(me.stopFlight(flight));
        }
        for (const {activeBusLookup, realtimeBuses} of me.gtfs.values()) {
            for (const bus of activeBusLookup.values()) {
                promises.push(me.stopBus(bus));
            }
            realtimeBuses.clear();
        }
        me.removing = true;
        Promise.all(promises).then(() => {
            delete me.removing;
            delete me.lastRefresh;
            delete me.lastRealtimeCheck;
        });
    }

    resetRailwayStatus() {
        for (const railway of this.railways.getAll()) {
            delete railway.status;
            delete railway.text;
            delete railway.suspended;
        }
    }

    refreshTrainTimetableData() {
        const me = this;

        showLoader(me.container);
        me.timetables.clear();
        loadTimetableData(me.dataUrl, me.clock).then(data => {
            me.timetables = new TrainTimetables(data, me.dataReferences);
            delete me.lastRefresh;
            delete me.lastRealtimeCheck;
            hideLoader(me.container);
        });
    }

    refreshBusData(replace) {
        const me = this,
            map = me.map,
            gtfsSources = me.dataSources.filter(source => source.gtfsUrl && !isExpired(source)),
            sourceIds = new Set(gtfsSources.map(({id}) => id)),
            deleteGtfs = ({id, activeBusLookup, layerIds, routeGroupIndex, colorGroupIndex}) => {
                const styleOpacities = me.styleOpacities,
                    promises = [];

                for (const layerId of layerIds) {
                    me.removeLayer(layerId);
                    for (let i = 0, ilen = styleOpacities.length; i < ilen; i++) {
                        if (styleOpacities[i].id === layerId) {
                            styleOpacities.splice(i, 1);
                            break;
                        }
                    }
                }
                map.removeSource(id);
                for (const bus of activeBusLookup.values()) {
                    promises.push(me.stopBus(bus));
                }
                me.gtfs.delete(id);
                return Promise.all(promises).then(() => {
                    me.trafficLayer.removeRouteGroup(routeGroupIndex);
                    me.trafficLayer.removeColorGroup(colorGroupIndex);
                });
            };

        for (const [id, gtfs] of me.gtfs) {
            if (!sourceIds.has(id)) {
                deleteGtfs(gtfs);
            }
        }

        for (const source of gtfsSources) {
            const id = source.id;

            if (me.gtfsLoading.has(id) || (me.gtfs.has(id) && !replace)) {
                continue;
            }

            me.gtfsLoading.add(id);
            // The absolute time is passed (not the clock, which may not yet know
            // its time zone); the service date is derived in the worker from the
            // feed's agency_timezone.
            loadBusData(source, me.clock.getTime(), me.lang).then(data => {
                return me.initialized ? data : new Promise(resolve => {
                    me.once('initialized', () => resolve(data));
                });
            }).then(data => {
                return me.gtfs.has(id) ? deleteGtfs(me.gtfs.get(id)).then(() => data) : data;
            }).then(data => {
                const {agency, timezone, featureCollection, version} = data;

                // Establish the map's time zone from the first GTFS feed when it is
                // not already fixed (by loading the mini-tokyo-3d data), and set the
                // clock before the data is registered so the animation loop reads the
                // correct offset. A feed whose agency_timezone differs from the
                // established zone can't be rendered correctly by the single clock,
                // so it is dropped.
                if (me.timezone === undefined) {
                    me.timezone = timezone;
                    me.clock.setTimezone(timezone);
                } else if (timezone !== me.timezone) {
                    console.warn(`Mini Tokyo 3D: GTFS source "${id}" uses time zone "${timezone}", which differs from the map's time zone "${me.timezone}"; skipping it.`);
                    me.gtfsLoading.delete(id);
                    return;
                }

                const featureLookup = new Map(),
                    layerIds = new Set(),
                    routeData = [];

                featureEach(featureCollection, feature => {
                    const properties = feature.properties;

                    if (properties.type === 0) {
                        featureLookup.set(properties.id, feature);
                        routeData.push({id: `${id}.${properties.id}`, feature});
                    }
                });
                me.gtfs.set(id, {
                    id,
                    agency,
                    version,
                    featureLookup,
                    stops: new Dataset(GTFSStop, data.stops),
                    routes: new Dataset(GTFSRoute, data.routes),
                    trips: new Dataset(GTFSTrip, data.trips),
                    layerIds,
                    activeBusLookup: new Map(),
                    realtimeBuses: new Set(),
                    vehiclePositionUrl: source.vehiclePositionUrl,
                    color: source.color,
                    frequencyBased: source.frequencyBased,
                    routeGroupIndex: me.trafficLayer.addRouteGroup(routeData),
                    // 피드 하나에 색 하나가 아니라 **노선마다** 등록한다. 서울 시내버스는
                    // 유형(간선/지선/광역/순환)이 곧 도색이라 한 색으로 묶으면 그 정보를
                    // 잃는다. 점유도 단계까지 미리 섞어 둔다 (위 BUS_OCCUPANCY_COLOR).
                    colorGroupIndex: me.trafficLayer.addColorGroup([
                        {id, color: source.color},
                        ...data.routes.flatMap(route => {
                            // 항목마다 서브컬러 4장. 버스가 읽는 건 color0(차체)과
                            // color3(지붕 삼각형)뿐이라 가운데 둘은 채우기용이다.
                            const base = route.color || source.color,
                                // color1·color2 는 버스가 안 쓰던 슬롯이다. 노선번호
                                // 글자를 여기 실어 지붕에 찍는다 (셰이더가 읽는다).
                                // 채우기용 base 를 그대로 두면 지붕에 아무 숫자나 뜬다.
                                [code0, code1] = encodeRoofNumber(route.shortName),
                                entry = accent => [base, code0, code1, accent];

                            return [
                                {id: `${id}.${route.id}`, color: entry(BUS_ACCENT_UNKNOWN)},
                                ...BUS_OCCUPANCY_COLOR.flatMap((occupancyColor, level) =>
                                    occupancyColor === undefined ? [] : [{
                                        id: `${id}.${route.id}.${level}`,
                                        color: entry(occupancyColor)
                                    }])
                            ];
                        })
                    ])
                });

                map.addSource(id, {
                    type: 'geojson',
                    data: featureCollection,
                    promoteId: 'id'
                });

                for (const key of ['busroute', 'busroute-highlighted']) {
                    const width = key === 'busroute' ? ['get', 'width'] : ['*', ['get', 'width'], 4];

                    me.addLayer({
                        id: `${key}-${id}-og-`,
                        type: 'line',
                        source: id,
                        filter: ['==', ['get', 'type'], 0],
                        // mapbox 기본값은 line-join: miter 다. 버스 노선 경로는 교차로·램프에서
                        // 실제로 급하게 꺾이는데, miter 는 그 코너를 뾰족한 첨점으로 늘려
                        // 계단처럼 보이게 한다 (선 굵기의 몇 배까지 뻗는다). round 는 굵기
                        // 반지름으로 둥글려 그 증폭을 없앤다. 철도 노선은 원본 데이터가
                        // 완만해서 이 문제를 겪지 않아 상류에 설정이 없었다.
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': {
                                'busroute': ['get', 'color'],
                                'busroute-highlighted': ['string', ['feature-state', 'route-highlight'], ['feature-state', 'trip-highlight'], ['get', 'color']]
                            }[key],
                            'line-opacity': {
                                'busroute': [
                                    'case',
                                    ['to-boolean', ['feature-state', 'hidden']],
                                    0,
                                    1
                                ],
                                'busroute-highlighted': [
                                    'case',
                                    [
                                        'all',
                                        [
                                            'any',
                                            ['to-boolean', ['feature-state', 'route-highlight']],
                                            ['to-boolean', ['feature-state', 'trip-highlight']],
                                        ],
                                        ['!', ['to-boolean', ['feature-state', 'hidden']]]
                                    ],
                                    1,
                                    0
                                ]
                            }[key],
                            'line-width': [
                                'interpolate',
                                ['exponential', 2],
                                ['zoom'],
                                11,
                                ['/', width, 2],
                                12,
                                width,
                                19,
                                width,
                                22,
                                ['*', width, 8]
                            ]
                        },
                        metadata: {
                            'busroute': {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-route': 0.1,
                                'mt3d:opacity-underground': 0,
                                'mt3d:opacity-underground-route': 0
                            },
                            'busroute-highlighted': {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-route': 0.1,
                                'mt3d:opacity-underground': 0.25,
                                'mt3d:opacity-underground-route': 0.1
                            }
                        }[key]
                    }, 'railways-og-13');
                    layerIds.add(`${key}-${id}-og-`);
                }

                // 정류장 마커. 점 피처 하나에 circle 레이어 하나다 (상류는 줌 5단계
                // x 2레이어 = 10개에 줌별 폴리곤을 깔았다. 워커 주석 참조).
                // 반지름 2.6px = 옛 지상 반지름 20m 를 z14·위도 37.5 에서 환산한 값
                // (해상도 7.58 m/px). 외곽선은 옛 코드처럼 z19~22 에서 굵어진다.
                me.addLayer({
                    id: `busstops-${id}-og-`,
                    type: 'circle',
                    source: id,
                    filter: ['==', ['get', 'type'], 2],
                    paint: {
                        'circle-radius': 2.6,
                        'circle-color': '#FFFFFF',
                        'circle-opacity': .7,
                        'circle-stroke-color': '#000000',
                        'circle-stroke-width': ['interpolate', ['exponential', 2], ['zoom'], 19, 2, 22, 16],
                        'circle-stroke-opacity': .7
                    },
                    metadata: {
                        'mt3d:opacity-effect': true,
                        'mt3d:opacity': 1,
                        'mt3d:opacity-route': 0.1,
                        'mt3d:opacity-underground': 0.25,
                        'mt3d:opacity-underground-route': 0.1
                    }
                }, 'railways-og-13');
                layerIds.add(`busstops-${id}-og-`);

                me.addLayer({
                    id: `busstops-poi-${id}`,
                    type: 'symbol',
                    source: id,
                    filter: ['==', ['get', 'type'], 2],
                    layout: {
                        'text-field': ['get', 'name'],
                        // OpenFreeMap 은 `Open Sans Bold`(Mapbox 시절 스택)를
                        // 제공하지 않는다 - 글리프가 404 면 그 레이어의 **라틴·숫자만**
                        // 조용히 빠진다(한글은 localIdeographFontFamily 로 로컬 렌더돼
                        // 살아 있어서 눈에 잘 안 띈다). 정류장 이름에 숫자가 흔하다.
                        'text-font': ['Noto Sans Bold'],
                        'text-max-width': 9,
                        'text-padding': 2,
                        'text-size': 12,
                        'text-anchor': 'bottom',
                        'text-offset': [0, -1]
                    },
                    paint: {
                        'text-color': 'rgba(102,102,102,1)',
                        'text-halo-blur': 0.5,
                        'text-halo-color': 'rgba(255,255,255,1)',
                        'text-halo-width': 1
                    },
                    minzoom: 14
                });
                layerIds.add(`busstops-poi-${id}`);

                const styleOpacities = me.styleOpacities,
                    existingLayerIds = new Set(styleOpacities.map(({id}) => id));

                for (const item of helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect')) {
                    if (!existingLayerIds.has(item.id)) {
                        styleOpacities.push(item);
                    }
                }
                me.refreshMap();

                if (me.searchMode === 'none') {
                    if (me.clockMode === 'playback') {
                        me.refreshBuses(id);
                    } else if (me.clockMode === 'realtime') {
                        me.refreshRealtimeBusData(id);
                    }
                }

                me.gtfsLoading.delete(id);
            }).catch(() => {
                me.gtfsLoading.delete(id);
            });
        }

        me.updateBusRouteVisibility();
    }

    refreshRealtimeTrainData() {
        const me = this;

        loadDynamicTrainData(me.dataSources).then(({trainData, trainInfoData}) => {
            const {activeTrainLookup, standbyTrainLookup, realtimeTrains, dataReferences} = me,
                now = me.clock.getTimeOffset();

            me.resetRailwayStatus();

            for (const trainInfoRef of trainInfoData) {
                const railway = me.railways.get(trainInfoRef.railway),
                    status = trainInfoRef.status;

                // Train information text is provided in Japanese only
                if (railway && status && status.ja) {
                    railway.status = status.ja;
                    railway.text = trainInfoRef.text.ja;
                }

                if (trainInfoRef.suspended) {
                    railway.suspended = true;
                }
            }

            standbyTrainLookup.clear();
            realtimeTrains.clear();

            // 지연 피드는 '지금 지연된 편성' 만 담는다. 지연이 풀린 편성은 그냥
            // 빠지는데, 아무도 delay 를 0 으로 되돌리지 않아 마커가 운행이 끝날 때까지
            // 남았다. 빠진 편성에 delay 0 항목을 만들어 넣어 지연이 바뀔 때와 똑같은
            // 경로를 타게 한다 — 아래 루프가 정지시킨 뒤 시간표로 다시 출발시킨다.
            // 피드를 못 받으면 여기까지 오지 않으므로(요청 실패는 reject) 통신 장애가
            // 지연을 지우는 일은 없다.
            const feedTrainIDs = new Set(trainData.map(({id}) => id));

            for (const train of activeTrainLookup.values()) {
                if (train.delay && !feedTrainIDs.has(train.id)) {
                    trainData.push({id: train.id, delay: 0});
                }
            }

            for (const trainRef of trainData) {
                const {id, r, n, y, d, os, ds, ts, fs, v, ad, delay, carComposition} = trainRef,
                    aliasId = id.replace('.Marunouchi.', '.MarunouchiBranch.');

                if (trainRef.date > (me.lastDynamicUpdate[trainRef.o] || '')) {
                    me.lastDynamicUpdate[trainRef.o] = trainRef.date;
                }
                realtimeTrains.add(trainRef.id);

                // Retry lookup replacing Marunouchi line with MarunouchiBranch line
                const activeTrain = activeTrainLookup.get(id) || activeTrainLookup.get(aliasId);

                let marked, tracked;

                // Update the avtive train if exists
                if (activeTrain) {
                    if ((y && y !== activeTrain.y.id) ||
                        (os && activeTrain.os && os[0] !== activeTrain.os[0].id) ||
                        (ds && activeTrain.ds && ds[0] !== activeTrain.ds[0].id) ||
                        (v && v !== (activeTrain.v || {}).id) ||
                        (ad && !activeTrain.ad) ||
                        (!isNaN(carComposition) && carComposition !== activeTrain.carComposition) ||
                        (!isNaN(delay) && delay !== activeTrain.delay)) {
                        marked = me.markedObject === activeTrain;
                        tracked = me.trackedObject === activeTrain;
                        me.stopTrain(activeTrain);
                    } else {
                        if (!activeTrain.timetable) {
                            activeTrain.update({ts, fs}, dataReferences);
                        }
                        continue;
                    }
                }

                let timetables = me.timetables.getByTrainId(id);

                // Retry lookup replacing Marunouchi line with MarunouchiBranch line
                if (timetables.length === 0) {
                    timetables = me.timetables.getByTrainId(aliasId);
                }

                // Start train with timetable
                if (timetables.length !== 0) {
                    for (const timetable of timetables) {
                        const train = new Train(timetable);

                        train.update({y, os, ds, v, ad, delay, carComposition}, dataReferences);
                        if (timetable.start + (delay || 0) <= now && now <= timetable.end + (delay || 0)) {
                            me.trainStart(train, {marked, tracked});
                        } else {
                            standbyTrainLookup.set(timetable.id, train);
                        }
                    }
                    continue;
                }

                if (!r) {
                    continue;
                }

                // Start train without timetable
                me.trainStart(new Train({id, r, n, y, d, os, ds, ts, fs, delay, carComposition}, dataReferences));
            }

            // Stop trains if they are no longer active
            for (const train of activeTrainLookup.values()) {
                const railway = train.r;

                if ((((railway.status && railway.dynamic) || !train.timetable) && !realtimeTrains.has(train.id)) || railway.suspended) {
                    me.stopTrain(train);
                }
            }

            me.refreshTrains();
            me.aboutPanel.updateContent();
        }).catch(error => {
            me.refreshTrains();
            console.log(error);
        });
    }

    refreshRealtimeFlightData() {
        const me = this;

        loadDynamicFlightData(me.dataSources).then(({atisData, flightData}) => {
            if (!atisData) {
                return;
            }

            const flightLookup = me.flightLookup,
                {landing, departure} = atisData,
                pattern = [landing.join('/'), departure.join('/')].join(' '),
                codeShareFlights = {},
                flightQueue = {};
            let arrRoutes = {},
                depRoutes = {},
                north = true;

            if (me.flightPattern !== pattern) {
                me.flightPattern = pattern;
                me.lastFlightPatternChanged = Date.now();
                for (const flight of me.activeFlightLookup.values()) {
                    me.stopFlight(flight);
                }
            }

            if (helpers.includes(landing, ['R16L', 'R16R'])) { // South wind, good weather, rush hour
                arrRoutes = {S: 'R16L', N: 'R16R'};
                depRoutes = {S: '22', N: '16R'};
                north = false;
            } else if (helpers.includes(landing, ['L22', 'L23'])) { // South wind, good weather
                arrRoutes = {S: 'L23', N: 'L22'};
                depRoutes = {S: 'O16R', N: '16L'};
                north = false;
            } else if (helpers.includes(landing, ['I16L', 'I16R'])) { // South wind, bad weather, rush hour
                arrRoutes = {S: 'I16L', N: 'I16R'};
                depRoutes = {S: '22', N: '16R'};
                north = false;
            } else if (helpers.includes(landing, ['I22', 'I23'])) { // South wind, bad weather
                arrRoutes = {S: 'I23', N: 'I22'};
                depRoutes = {S: '16R', N: '16L'};
                north = false;
            } else if (helpers.includes(landing, ['I34L', 'H34R'])) { // North wind, good weather
                arrRoutes = {S: 'IX34L', N: 'H34R'};
                depRoutes = {S: '05', N: '34R'};
                north = true;
            } else if (helpers.includes(landing, ['I34L', 'I34R'])) { // North wind, bad weather
                arrRoutes = {S: 'IZ34L', N: 'H34R'};
                depRoutes = {S: '05', N: '34R'};
                north = true;
            } else if (landing.length !== 1) {
                console.log(`Unexpected RWY: ${landing}`);
            } else { // Midnight
                if (helpers.includes(landing, 'I23')) {
                    arrRoutes = {S: 'IY23', N: 'IY23'};
                    north = false;
                } else if (helpers.includes(landing, 'L23')) {
                    arrRoutes = {S: 'LY23', N: 'LY23'};
                    north = false;
                } else if (helpers.includes(landing, 'I34L')) {
                    arrRoutes = {S: 'IX34L', N: 'IX34L'};
                    north = true;
                } else if (helpers.includes(landing, 'I34R')) {
                    arrRoutes = {S: 'IY34R', N: 'IY34R'};
                    north = true;
                } else if (helpers.includes(landing, 'L22')) { // Special
                    arrRoutes = {S: 'L22', N: 'L22'};
                    north = false;
                } else {
                    console.log(`Unexpected LDG RWY: ${landing[0]}`);
                }
                if (helpers.includes(departure, '16L')) {
                    depRoutes = {S: 'N16L', N: 'N16L'};
                } else if (helpers.includes(departure, '05')) {
                    depRoutes = {S: 'N05', N: 'N05'};
                } else if (helpers.includes(departure, '16R')) { // Special
                    depRoutes = {S: '16R', N: '16R'};
                } else {
                    console.log(`Unexpected DEP RWY: ${departure[0]}`);
                }
            }

            // Create code share flight lookup
            for (const flightRef of flightData) {
                if (helpers.includes(AIRLINES_FOR_ANA_CODE_SHARE, flightRef.a)) {
                    const {dp, ds, sdt, or, ar, sat} = flightRef,
                        key = `${dp || or}.${ds || ar}.${sdt || sat}`;

                    codeShareFlights[key] = flightRef;
                }
            }

            for (const flightRef of flightData) {
                const {id, n, dp, ds, sdt, or, ar, sat} = flightRef;
                let flight = flightLookup.get(id),
                    status = flightRef.s,
                    {maxFlightSpeed: maxSpeed, flightAcceleration: acceleration} = configs;

                // Check code share flight
                if (id.match(/NH\d{4}$/)) {
                    const key = `${dp || or}.${ds || ar}.${sdt || sat}`,
                        codeShareFlight = codeShareFlights[key];

                    if (codeShareFlight) {
                        codeShareFlight.n.push(...n);
                        continue;
                    }
                }

                if (!flight) {
                    if (helpers.includes(['Cancelled', 'PostponedTomorrow'], status)) {
                        continue;
                    }
                    const airport = me.airports.get(ds || or),
                        direction = airport ? airport.direction : 'S',
                        route = dp === 'NRT' ? `NRT.${north ? '34L' : '16R'}.Dep` :
                        ar === 'NRT' ? `NRT.${north ? '34R' : '16L'}.Arr` :
                        dp === 'HND' ? `HND.${depRoutes[direction]}.Dep` :
                        ar === 'HND' ? `HND.${arrRoutes[direction]}.Arr` : undefined,
                        feature = me.featureLookup.get(route);

                    if (feature) {
                        flight = new Flight({
                            id,
                            n,
                            a: flightRef.a,
                            dp,
                            ar,
                            ds,
                            or
                        }, {
                            airports: me.airports,
                            operators: me.operators
                        });
                        flight.runway = route.replace(/^([^.]+\.)[A-Z]*([^.]+).+/, '$1$2');
                        flight.feature = feature;
                        flightLookup.set(flight.id, flight);
                    } else {
                        continue;
                    }
                }
                flight.update({
                    edt: flightRef.edt,
                    adt: flightRef.adt,
                    sdt,
                    eat: flightRef.eat,
                    aat: flightRef.aat,
                    sat
                });

                const departureTime = helpers.valueOrDefault(flight.edt, helpers.valueOrDefault(flight.adt, flight.sdt)),
                    arrivalTime = helpers.valueOrDefault(flight.eat, helpers.valueOrDefault(flight.aat, flight.sat));

                if (arrivalTime !== undefined && !status) {
                    if (arrivalTime < flight.sat) {
                        status = 'NewTime';
                    } else if (arrivalTime > flight.sat) {
                        status = 'Delayed';
                    } else if (arrivalTime === flight.sat) {
                        status = 'OnTime';
                    }
                } else if (departureTime !== undefined && (!status || status === 'CheckIn' || status === 'NowBoarding' || status === 'FinalCall' || status === 'BoardingComplete' || status === 'Departed')) {
                    if (departureTime < flight.sdt) {
                        status = 'NewTime';
                    } else if (departureTime > flight.sdt) {
                        status = 'Delayed';
                    } else if (departureTime === flight.sdt) {
                        status = 'OnTime';
                    }
                }
                flight.update({s: status}, {flightStatuses: me.flightStatuses});

                if (arrivalTime !== undefined) {
                    maxSpeed /= 2;
                    acceleration /= -2;
                }

                const duration = maxSpeed / Math.abs(acceleration) / 2 + flight.feature.properties.length / maxSpeed,
                    standingDuration = configs.standingDuration;

                if (departureTime) {
                    flight.start = flight.base = departureTime;
                    flight.entry = flight.start - standingDuration;
                    flight.end = flight.start + duration;
                } else {
                    flight.start = flight.entry = arrivalTime - duration;
                    flight.base = flight.start + duration - standingDuration;
                    flight.end = flight.start + duration + standingDuration;
                }
                flight.maxSpeed = maxSpeed;
                flight.acceleration = acceleration;

                const queue = flightQueue[flight.runway] = flightQueue[flight.runway] || [];
                queue.push(flight);

                if (flightRef.date > (me.lastDynamicUpdate[flightRef.o] || '')) {
                    me.lastDynamicUpdate[flightRef.o] = flightRef.date;
                }
            }

            for (const key of Object.keys(flightQueue)) {
                const queue = flightQueue[key];
                let latest = 0;

                queue.sort((a, b) => a.base - b.base);
                for (const flight of queue) {
                    const base = flight.base,
                        delay = Math.max(base, latest + configs.minFlightInterval) - base;

                    if (delay) {
                        flight.start += delay;
                        flight.base += delay;
                        flight.entry += delay;
                        flight.end += delay;
                    }
                    latest = flight.base;
                }
            }

            me.refreshFlights();
            me.aboutPanel.updateContent();
        }).catch(error => {
            me.refreshFlights();
            console.log(error);
        });
    }

    refreshRealtimeBusData(gtfsId) {
        const me = this;

        for (const gtfs of gtfsId ? [me.gtfs.get(gtfsId)] : me.gtfs.values()) {
            const {id: gtfsId, vehiclePositionUrl, featureLookup, stops, routes, trips, activeBusLookup, realtimeBuses} = gtfs;

            if (!vehiclePositionUrl) {
                me.refreshBuses(gtfsId);
                continue;
            }

            loadDynamicBusData(vehiclePositionUrl).then(data => {
                realtimeBuses.clear();

                // 차량번호 -> 지금 그려지고 있는 실시간 버스. **회차에서 트립이
                // 바뀌어도 객체를 살려 갈아태우려면** 트립이 아니라 차량으로
                // 찾아야 한다 (activeBusLookup 은 트립 키다).
                const byVehicle = new Map();

                for (const b of activeBusLookup.values()) {
                    if (b.anchor && b.id) {
                        byVehicle.set(b.id, b);
                    }
                }
                gtfs.date = me.clock.getString(data.header.timestamp * 1000);

                // 피드 신선도. **`header.timestamp` 가 아니라 차량별 관측 시각의
                // 최댓값**을 쓴다 - 헤더는 파일을 쓴 시각(30초마다)이라 노선이 실제로
                // 언제 관측됐는지를 말해 주지 않는다. 시계 옆에 '관측 N초 전 · 갱신 M초'
                // 로 나간다 (clock-control.setFeedFreshness).
                let newestObservation = 0;

                for (const {id, vehicle: vp} of data.entity) {
                    if (!vp) {
                        continue;
                    }

                    const vehicle = vp.vehicle,
                        stop = vp.currentStopSequence,
                        position = vp.position,
                        tripId = vp.trip && vp.trip.tripId,
                        observedAt = Number(vp.timestamp) * 1000;

                    if (observedAt > newestObservation) {
                        newestObservation = observedAt;
                    }

                    if (!(stop || position) || !tripId) {
                        continue;
                    }

                    realtimeBuses.add(tripId);

                    const trip = trips.get(tripId),
                        feature = trip && featureLookup.get(trip.shape),
                        route = trip && routes.get(trip.route);

                    if (!trip || !feature || !route || route.hidden) {
                        continue;
                    }

                    const isActive = activeBusLookup.has(tripId);
                    let bus;

                    if (isActive) {
                        bus = activeBusLookup.get(tripId);

                        // ⚠️ **같은 관측을 두 번 적용하지 않는다.** 엔진은 15초마다
                        // 피드를 다시 읽는데(configs.realtimeCheckInterval) 관측은
                        // 60초에 한 번 갱신된다(routes.json `every: 2`). 가드가 없으면
                        // 한 관측이 **네 번** 적용되고, 그때마다 아래 deadReckonOffset
                        // 이 '관측 나이' 로 위치를 다시 깔아 **분당 네 번 보정이 눈에
                        // 보인다**. 그 사이 애니메이션은 접근 감속(sectionPace)으로
                        // 달리므로 두 모델이 어긋난 만큼 버스가 튄다.
                        // 관측은 한 번만 반영하고 그 사이는 애니메이션이 책임진다.
                        // `vp.timestamp` 가 없으면 NaN !== NaN 이라 종전대로 매번 적용된다.
                        if (bus.observedAt === observedAt) {
                            continue;
                        }
                    } else {
                        let offset = 0;
                        const offsets = trip.stops.map(stopId =>
                            // Use the previous offset to calulate a weight and pick a closer point
                            (offset = nearestCloserPointOnLine(feature, stops.get(stopId).coord, offset).properties.totalDistance)
                        );
                        const newId = vehicle ? vehicle.licensePlate || vehicle.id : id,
                            // **회차: 같은 차량이 트립을 갈아탄다.** 노선 형상이
                            // 방향별이라 한 바퀴가 두 트립이고, 둘이 회차 정류장에서
                            // 만난다 (15번: did0 seq 1~38 · did1 39~75, 미금역).
                            // 새 객체를 만들면 옛 버스가 사라지고 새 버스가 나타난다
                            // - 화면에서 끊기고 **추적·팝업도 끊긴다**
                            // (사용자 신고 2026-09-16, 두 번).
                            // 객체를 살린 채 새 트립으로 갈아태운다.
                            carried = byVehicle.get(newId);

                        if (carried && carried.trip.id !== tripId) {
                            activeBusLookup.delete(carried.trip.id);
                            byVehicle.delete(newId);
                            animation.stop(carried.animationID);
                            // 형상이 바뀌므로 렌더 인스턴스는 다시 만들어야 한다
                            // (routeIndex 가 `gtfsId.shape` 에서 나온다). 객체
                            // **참조**는 그대로라 markedObject/trackedObject 가 산다.
                            me.trafficLayer.removeObject(carried);
                            carried.trip = trip;
                            carried.feature = feature;
                            carried.offsets = offsets;
                            // 새 형상은 누적거리 원점이 달라 옛 닻이 뜻을 잃는다.
                            // 두 지점은 물리적으로 같은 자리라 점프로 안 보인다.
                            carried.offset = 0;
                            delete carried.anchor;
                            delete carried.prevAnchor;
                            bus = carried;
                        } else {
                            bus = new Bus({
                                id: newId,
                                gtfsId,
                                trip,
                                feature,
                                offsets,
                                offset: 0
                            });
                        }
                    }
                    // 점유도. 없으면 undefined 로 두고 노선색을 유지한다 -
                    // '데이터 없음' 과 '한산함' 은 다른 정보다 (열차와 같은 판단).
                    const occupancy = vp.occupancyStatus;

                    if (isActive && bus.occupancy !== occupancy) {
                        bus.occupancy = occupancy;
                        me.trafficLayer.setObjectColorIndex(bus, me.trafficLayer.getBusColorIndex(bus));
                    } else {
                        bus.occupancy = occupancy;
                    }
                    // 트립을 이어받은 차량. **두 경로 공통**이다 - 좌표 경로에서만
                    // 갱신하면 순번 경로(seg_pt 가 없는 구간)에서 팝업이 옛 차량번호를
                    // 붙들고 있는다.
                    const busId = vehicle ? vehicle.licensePlate || vehicle.id : id,
                        sameBus = bus.id === busId;

                    bus.id = busId;
                    if (stop) {
                        bus.stop = stop;
                    } else {
                        // **궤적의 닻을 새로 놓는다.** 관측 나이만큼 미리 밀지 않는다 -
                        // 시간은 `trajectory.advance` 가 그리는 순간에 흘려보낸다.
                        // 미리 밀던 때는 '얼마나 밀지' 의 상한(`maxDeadReckoningAge`)과
                        // 구간 수 캡이 필요했고, 그 캡에 걸리면 버스가 그 자리에
                        // 얼어붙었다 (2026-09-15 사용자 신고).
                        const at = Number(vp.timestamp) * 1000,
                            now = me.clock.getTime(),
                            observed = nearestCloserPointOnLine(
                                feature, [position.longitude, position.latitude], bus.offset
                            ).properties.totalDistance;

                        // 갈아타기 전에 지금 그려지던 자리를 남긴다 - 거기서 새
                        // 궤적으로 `BLEND_MS` 동안 섞는다 (순간이동 방지).
                        //
                        // **같은 차량이면 얼마나 뒤든 섞는다.** 3D 가 전광판보다
                        // 앞서 갔으면 되돌아오는 게 맞다 - 되돌아감도 블렌드라
                        // 순간이동이 아니라 미끄러짐으로 보인다.
                        //
                        // ⚠️ **다른 차량이 이 트립을 이어받았으면 섞지 않는다.**
                        // 차량이 종점에 닿아 방향이 바뀌면 write-gtfsrt 가 슬롯을
                        // 다시 잡고, 비어 버린 트립을 다른 차량이 가져간다. 엔진은
                        // 트립으로 버스를 식별하므로 그때 섞으면 **노선을 가로질러**
                        // 미끄러진다 (2026-09-16 사용자 신고: 같은 자리에서 차량번호만
                        // 바뀌더니 위치가 이상해졌다).
                        //
                        // 가르는 기준이 '몇 정류장' 이 아니라 **차량 동일성**인 이유:
                        // 거리로 자르면 되돌아올 수 있는 폭에 임의의 상한이 생기는데,
                        // 정작 틀린 경우(차량 교체)는 거리와 무관하게 틀리다.
                        // 블렌드 시간은 **보정 거리로 정한다** - 고정하면
                        // 화면속도가 거리에 비례해 순간이동으로 보인다
                        // (2026-09-16 실측 103~315 km/h).
                        const drawn = bus.anchor && sameBus ? positionAt(bus.anchor,
                            bus.prevAnchor, bus.offsets, trip.departureTimes, now) : null;

                        bus.prevAnchor = drawn === null ? null :
                            {t: now, s: drawn, ms: blendMs(observed - drawn)};
                        bus.anchor = {t: Number.isFinite(at) ? at : now, s: observed};
                        bus.offset = observed;
                    }
                    bus.observedAt = observedAt;
                    if (!isActive) {
                        me.busStart(bus);
                    } else {
                        me.busTrack(bus);
                    }
                }

                // **회차에서 한 차량이 트립을 갈아탄다.** 노선 형상이 방향별로
                // 갈려 있어 15번은 did0 = seq 1~38, did1 = 39~75 이고 둘이
                // 미금역에서 만난다 (실측: 차량당 하루 3회). 엔진은 트립으로
                // 버스를 식별하므로 갈아타는 순간 **옛 트립의 버스가 그대로
                // 남는다** - `busTrack` 이 다음 틱(최대 15초)에 종점 도달을
                // 보고서야 지운다. 그 사이 같은 차가 두 대로 그려지고, 지워질
                // 때 한 대가 사라진다 (사용자 신고 2026-09-16: 미금역에서
                // 나타났다 사라졌다).
                //
                // 새 트립이 이미 그 차량을 들고 있으면 옛 것은 **이번 패스에서**
                // 지운다. 같은 프레임이라 겹침도 공백도 없다.
                // 시간표 버스(`anchor` 없음)와 차량번호 없는 버스는 건드리지
                // 않는다 - 전자는 이 경로로 오지 않고, 후자는 id 로 짝지을 수 없다.
                for (const bus of staleTrips(activeBusLookup.values(), realtimeBuses)) {
                    me.stopBus(bus);
                }

                // 갱신 주기는 **화면이 실제로 새 위치를 받은 간격**이다. 상수로
                // 박으면 `routes.json` 의 `every` 나 쿼터 사정이 바뀔 때 화면만
                // 거짓말을 한다. 첫 폴링에는 이전 값이 없어 나이만 보여준다.
                //
                // ⚠️ **관측 시각의 간격으로 재면 안 된다.** 주기가 세 층이라
                // (관측 60초 · 피드 재작성 30초 · 이 함수의 재읽기 15초) 관측
                // 간격으로 카운트다운을 깔면 0 에 닿은 뒤 최대 한 읽기 주기만큼
                // '갱신 중' 에 머문다 - 매분 4분의 1을 멈춰 있는 것처럼 보였다.
                // 받은 시각으로 재면 세 층이 한 숫자로 접힌다.
                if (newestObservation && newestObservation !== me.feedObservedAt) {
                    if (me.feedObservedAt) {
                        // ⚠️ **주기는 관측 시각의 간격으로 잰다. 받은 간격이 아니다.**
                        // 받은 간격은 접속 직후 반토막이 섞인다 - 페이지를 주기 중간에
                        // 열면 첫 두 수신이 몇 초 차이로 붙고, 그 값이 주기로 굳으면
                        // 멀쩡한 피드에 '갱신 멈춤' 이 뜬다 (실측 2026-09-14 22:22:08).
                        // 관측 시각 간격은 폴링 시각이라 항상 온전한 한 주기다.
                        // 이어받기·결번·탭 비활성으로 튈 수 있어 범위는 자른다.
                        me.feedPeriod = Math.min(300000, Math.max(15000, newestObservation - me.feedObservedAt));
                    }
                    me.feedObservedAt = newestObservation;
                    // 카운트다운의 **기점**만 받은 시각이다 (clock-control 참조).
                    me.feedReceivedAt = Date.now();
                }
                if (me.clockControl) {
                    me.clockCtrl.setFeedFreshness(me.feedObservedAt, me.feedPeriod, me.feedReceivedAt);
                }

                me.aboutPanel.updateContent();
            }).catch(error => {
                console.log(error);
            });
        }
    }

    updateUndergroundButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.maplibregl-ctrl-underground');

        if (button) {
            const classList = button.classList;

            // 제목은 누르면 무엇이 되는지를 적는다 (다음 상태).
            button.title = dict[{
                both: 'enter-ground', ground: 'enter-underground', underground: 'enter-both-levels'
            }[mode]];
            classList.toggle('maplibregl-ctrl-underground-visible', mode === 'underground');
            classList.toggle('maplibregl-ctrl-underground-both', mode === 'both');
        }
    }

    updatePlaybackButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.maplibregl-ctrl-playback');

        if (button) {
            const classList = button.classList;

            if (mode === 'playback') {
                button.title = dict['exit-playback'];
                classList.add('maplibregl-ctrl-playback-active');
            } else {
                button.title = dict['enter-playback'];
                classList.remove('maplibregl-ctrl-playback-active');
            }
        }
    }

    /**
     * Hops from one running train to the next, so the map can just be watched.
     * @param {boolean} on - True to start hopping
     */
    /**
     * Turns congestion colouring on or off.
     * @param {boolean} on - True to colour trains by congestion
     */
    _setCongestionMode(on) {
        const me = this,
            {container, dict} = me,
            button = container.querySelector('.maplibregl-ctrl-congestion');

        me.congestionMode = on;
        if (button) {
            button.title = dict[on ? 'exit-congestion' : 'enter-congestion'];
            button.classList.toggle('maplibregl-ctrl-congestion-active', on);
        }
        me.refreshCongestionColors();
    }

    /**
     * Hides every overlay so only the map is left. Restored by Escape or a map click —
     * the switch itself disappears with the rest of the UI.
     * @param {boolean} on - True to hide the UI
     */
    _setUIHidden(on) {
        const me = this;

        if (!!me.uiHidden === on) {
            return;
        }
        me.uiHidden = on;
        me.container.classList.toggle('ui-hidden', on);
    }

    _setCinemaMode(on) {
        const me = this,
            {container, dict} = me,
            button = container.querySelector('.maplibregl-ctrl-cinema');

        container.classList.toggle('cinema-mode', on);

        clearInterval(me.cinemaInterval);
        delete me.cinemaInterval;
        clearTimeout(me.cinemaRetryTimeout);
        delete me.cinemaRetryTimeout;
        me.cinemaMode = on;

        if (button) {
            button.title = dict[on ? 'exit-cinema' : 'enter-cinema'];
            button.classList.toggle('maplibregl-ctrl-cinema-active', on);
        }
        if (on) {
            // Circle the train from above; the plain 'position' camera sits too far
            // out to make watching worthwhile.
            me.cinemaTrackingMode = me.trackingMode;
            me._setTrackingMode('helicopter');
            me._trackRandomTrain();
            me.cinemaInterval = setInterval(() => me._trackRandomTrain(), me.cinemaDuration);
            me.updateCinemaControl();
            me.updateHandlersAndControls();
        } else {
            if (me.cinemaTrackingMode) {
                me._setTrackingMode(me.cinemaTrackingMode);
                delete me.cinemaTrackingMode;
            }
            me.trackObject();
            me.updateCinemaControl();
            me.updateHandlersAndControls();
        }
    }

    /**
     * Changes how long the cinema mode watches one train.
     * @param {number} duration - Milliseconds
     */
    _setCinemaDuration(duration) {
        const me = this;

        me.cinemaDuration = duration;
        if (me.cinemaMode) {
            // 주기가 바뀌면 타이머를 다시 건다. 지금 보고 있는 열차는 그대로 두고
            // 남은 시간만 새 주기로 다시 센다 — 바꿀 때마다 열차가 튀면 안 된다.
            clearInterval(me.cinemaInterval);
            me.cinemaInterval = setInterval(() => me._trackRandomTrain(), duration);
        }
        me.updateCinemaControl();
    }

    /**
     * Draws or removes the cinema mode's switch-interval buttons.
     */
    updateCinemaControl() {
        const me = this,
            {container, dict} = me;
        let control = container.querySelector('.cinema-ctrl');

        if (!me.cinemaMode) {
            if (control) {
                control.remove();
            }
            if (me.modeSegments) {
                me.modeSegments.classList.remove('hidden');
            }
            return;
        }

        if (!control) {
            // 상태 바 안에 넣는다. container 바닥에 두면 열차 패널이 같은 자리를 덮어
            // 모바일에서 안 보인다. 대신 모드 세그먼트를 잠시 비운다.
            control = helpers.createElement('div', {className: 'cinema-ctrl'}, me.statusBar);
            if (me.modeSegments) {
                me.modeSegments.classList.add('hidden');
            }
            for (const duration of configs.cinemaDurations) {
                const button = helpers.createElement('button', {
                    className: 'cinema-duration',
                    innerHTML: duration < 60000 ?
                        dict['seconds'].replace('$1', duration / 1000) :
                        dict['minutes'].replace('$1', duration / 60000)
                }, control);

                button.addEventListener('click', () => me._setCinemaDuration(duration));
                button.dataset.duration = duration;
            }
            helpers.createElement('div', {
                className: 'cinema-ctrl-label',
                innerHTML: dict['cinema-interval']
            }, control);
        }
        for (const button of control.querySelectorAll('.cinema-duration')) {
            button.classList.toggle('cinema-duration-active', +button.dataset.duration === me.cinemaDuration);
        }
    }

    _trackRandomTrain() {
        const me = this,
            // 버스도 고른다. 여기서 열차만 보던 탓에 bus.kgenots.com 은 시네마 모드가
            // 켜지기만 하고 아무것도 추적하지 못했다 - activeTrainLookup 이 늘 비어
            // 있어서 1초마다 재시도만 돌았다 (실측 2026-09-12: 라이브 13~15대가
            // 있는데 70초 동안 추적 0회, 같은 시각 metro 는 14초 만에 추적).
            // 버스 추적 자체는 이미 지원된다 - 클릭 경로가 trackObject(bus) 를 쓴다.
            candidates = [...me.activeTrainLookup.values()];

        for (const {activeBusLookup} of me.gtfs.values()) {
            candidates.push(...activeBusLookup.values());
        }

        clearTimeout(me.cinemaRetryTimeout);
        delete me.cinemaRetryTimeout;
        if (candidates.length) {
            me.trackObject(candidates[Math.floor(Math.random() * candidates.length)]);
        } else if (me.cinemaMode) {
            // 열차가 전부 정지한 직후에는 고를 대상이 없다. 다시 출발할 때까지 기다린다.
            me.cinemaRetryTimeout = setTimeout(() => me._trackRandomTrain(), 1000);
        }
    }

    /**
     * Moves the camera to the device's location and marks it, so the trains running
     * around you are the ones on screen.
     */
    showCurrentLocation() {
        const me = this,
            {container, dict} = me;

        if (!navigator.geolocation) {
            helpers.showNotification(container, dict['location-unavailable']);
            return;
        }

        navigator.geolocation.getCurrentPosition(({coords}) => {
            const center = [coords.longitude, coords.latitude];

            me._setCinemaMode(false);
            if (me.locationMarker) {
                me.locationMarker.remove();
            }
            me.locationMarker = new Marker({
                element: helpers.createElement('div', {className: 'location-marker'}),
                anchor: 'center'
            }).setLngLat(center).addTo(me.map);
            me.flyTo({center, zoom: Math.max(me.getZoom(), 15)});
        }, () => {
            helpers.showNotification(container, dict['location-unavailable']);
        });
    }

    updateEcoButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.maplibregl-ctrl-eco');

        if (button) {
            const classList = button.classList;

            if (mode === 'eco') {
                button.title = dict['exit-eco'];
                classList.add('maplibregl-ctrl-eco-active');
            } else {
                button.title = dict['enter-eco'];
                classList.remove('maplibregl-ctrl-eco-active');
            }
        }
    }

    refreshMap() {
        const me = this,
            {map, viewMode, searchMode} = me,
            isUndergroundMode = viewMode === 'underground',
            isNotSearchResultMode = searchMode === 'none' || searchMode === 'edit',
            // 배열은 우선순위다 — 먼저 정의된 키가 이긴다(setStyleOpacities). 'both' 는
            // 지하 레이어만 덧키를 갖고 나머지는 지상 값으로 떨어지므로, 배경·건물은
            // 지상 모드와 똑같이 그려진다.
            factorKeys = isUndergroundMode ? ['mt3d:opacity-underground'] :
            viewMode === 'both' ? ['mt3d:opacity-both', 'mt3d:opacity'] : ['mt3d:opacity'];

        helpersMapbox.setStyleOpacities(map, me.styleOpacities, isNotSearchResultMode ?
            factorKeys : [...factorKeys.map(key => `${key}-route`), ...factorKeys]);
        helpersMapbox.setSunlight(map, me.clock.getTime(), isUndergroundMode ? 0 : 1, true);
        me.setLayerVisibility('hd-area', isUndergroundMode ? 'none' : 'visible');
        me.setLayerVisibility('hd-elevated-area', isUndergroundMode ? 'none' : 'visible');
        me.trafficLayer.setMode(viewMode, searchMode);
    }

    updateLayerVisibility() {
        const me = this,
            layerVisibility = me.layerVisibility,
            hasTile3D = layerVisibility.tile3d.values().some(v => v === 'visible');

        for (const [id, visibility] of layerVisibility.model.entries()) {
            me.map.setLayoutProperty(id, 'visibility', hasTile3D ? 'none' : visibility);
        }
    }

    _setSearchMode(mode) {
        const me = this;

        if (me.searchMode !== mode) {
            me.searchMode = mode;
            me.stopAll();
            me.refreshMap();
        }
    }

    _setViewMode(mode) {
        const me = this;

        if (me.viewMode === mode) {
            return;
        }

        me.updateUndergroundButton(mode);
        me.viewMode = mode;
        me.refreshMap();
        me.fire({type: 'viewmode', mode});
    }

    _setTrackingMode(mode) {
        const me = this;

        if (me.trackingMode === mode) {
            return;
        }

        me.trackingMode = mode;
        // 모드를 다시 고르는 건 "자동 카메라를 되살려라"는 뜻이다.
        delete me.cameraOverride;
        if (isVehicle(me.trackedObject)) {
            me.updateBaseZoom();
            me.updateTrackingParams(true);
            me.updateHandlersAndControls();
            me.startViewAnimation();
        }
        me.fire({type: 'trackingmode', mode});
    }

    _setClockMode(mode) {
        const me = this;

        if (me.clockMode === mode) {
            return;
        }

        me.updatePlaybackButton(mode);
        me.clockMode = mode;
        me.clock.reset();
        me.onClockChange();
        if (me.clockControl) {
            me.clockCtrl.setMode(mode);
        }
        if (mode === 'playback') {
            me.resetRailwayStatus();
        }
        // 시계 모드가 바뀌면 열차가 전부 정지하고 추적도 풀린다(onClockChange).
        // 시네마는 다음 전환 주기까지 빈 화면을 보게 되므로 — 2분 주기면 2분이다 —
        // 여기서 곧바로 새 열차를 잡는다.
        if (me.cinemaMode) {
            me._trackRandomTrain();
        }
        me.fire({type: 'clockmode', mode});
    }

    _setEcoMode(mode) {
        const me = this;

        if (me.ecoMode === mode) {
            return;
        }

        me.updateEcoButton(mode);
        me.ecoMode = mode;
        me.fire({type: 'ecomode', mode});
    }

    /**
     * 시계 모드·날짜에 맞춰 버스 피드를 갈아끼운다.
     *
     * **재생 모드에는 실시간 피드가 없다.** 그래서 예전에는 재생으로 넘기면
     * 버스가 한 대도 안 나왔다 - 합성 시각표로 스폰하는 걸 `frequencyBased`
     * 가드가 막기 때문이다(그 가드는 옳다. 실측 사고: 05시에 271개 트립 동시
     * 활성). 대신 그 날짜의 **관측 시간표**로 갈아끼운다 - 그때 실제로 다닌
     * 운행만 트립이라 같은 문제가 안 생긴다 (09-08 실측: 동시 활성 최대 13개,
     * 실제 차량 15대). 시각이 관측이므로 그 피드에서는 가드를 끈다.
     *
     * 만드는 쪽은 seoul-bus-data 의 `tools/build-history-gtfs.py` 다.
     */
    updateBusFeedForClock() {
        const me = this,
            sources = me.dataSources.filter(source => source.historyUrl);

        if (!sources.length) {
            return;
        }
        // 날짜 목록을 한 번만 받는다. 못 받으면 재생에서 버스를 띄우지 않는다 -
        // 없는 zip 을 가리키면 로더가 실패한 채로 남는다.
        if (!me.busHistoryDays) {
            if (me.busHistoryLoading) {
                return;
            }
            me.busHistoryLoading = true;
            helpers.loadJSON(sources[0].historyUrl.replace(/[^/]*$/, 'index.json')).then(({days, default: hasDefault}) => {
                me.busHistoryDays = new Set(days);
                me.busHistoryDefault = !!hasDefault;
                delete me.busHistoryLoading;
                me.updateBusFeedForClock();
            }).catch(() => {
                me.busHistoryDays = new Set();
                delete me.busHistoryLoading;
            });
            return;
        }

        // 서비스일 경계(03:00)가 아니라 **달력 날짜**로 고른다. 관측 파일이
        // 달력 날짜로 갈리므로 00~03시 관측도 그 날짜 파일에 들어 있다.
        // 관측이 있는 날짜면 그 날짜를, 없으면 **대표일**을 쓴다. 대표일은 평균이
        // 아니라 관측이 가장 촘촘한 진짜 하루다 (평균을 계산하면 템플릿 x 배차가
        // 되어 실제로 없었던 운행 패턴이 나온다). 달력 전체 기간으로 굽혀 있어
        // 어느 날짜로 재생해도 돈다.
        const date = me.clock.getString().slice(0, 10).replace(/-/g, ''),
            day = me.busHistoryDays.has(date) ? date : (me.busHistoryDefault ? 'default' : null),
            playback = me.clockMode === 'playback' && day !== null;
        let changed = false;

        for (const source of sources) {
            if (source.liveGtfsUrl === undefined) {
                source.liveGtfsUrl = source.gtfsUrl;
                source.liveVehiclePositionUrl = source.vehiclePositionUrl;
                source.liveFrequencyBased = source.frequencyBased;
            }
            const url = playback ? source.historyUrl.replace('{date}', day) : source.liveGtfsUrl;

            if (source.gtfsUrl === url) {
                continue;
            }
            source.gtfsUrl = url;
            source.vehiclePositionUrl = playback ? undefined : source.liveVehiclePositionUrl;
            source.frequencyBased = playback ? false : source.liveFrequencyBased;
            changed = true;
        }
        if (changed) {
            me.refreshBusData(true);
        }
    }

    onClockChange() {
        const me = this,
            baseTime = me.clock.getTime('03:00');

        me.stopAll();
        me.markObject();
        me.trackObject();
        me.updateBusFeedForClock();

        if (me.lastTimetableRefresh !== baseTime) {
            me.refreshTrainTimetableData();
            me.lastTimetableRefresh = baseTime;
        }
    }

    /**
     * Returns the lights currently set on the map. The returned object has the
     * same shape as the payload of the {@link Map#event:light} event.
     * @returns {Object} The current lights, with the following properties:
     *     - directional: The directional light ({color, intensity, direction})
     *     - ambient: The ambient light ({color, intensity})
     *     - brightness: The perceived brightness (0-1)
     */
    getLight() {
        const map = this.map;

        return {
            directional: helpersMapbox.getDirectionalLight(map),
            ambient: helpersMapbox.getAmbientLight(map),
            brightness: helpersMapbox.getBrightness(map)
        };
    }

    /**
     * 클릭 지점의 정류장을 찾아 도착예정 패널을 띄운다.
     * @param {Object} point - Screen point to pick
     */
    showBusStopArrivals(point) {
        const me = this,
            map = me.map,
            layerIds = [];

        for (const id of me.gtfs.keys()) {
            const layerId = `busstops-${id}-og-`;

            if (map.getLayer(layerId)) {
                layerIds.push(layerId);
            }
        }
        if (layerIds.length === 0) {
            return;
        }

        // 정류장 원은 반지름 2.6px 이라 손가락으로는 못 맞힌다. 상자로 넓혀 잡는다.
        const [x, y] = [point.x, point.y],
            pad = 8,
            feature = map.queryRenderedFeatures(
                [[x - pad, y - pad], [x + pad, y + pad]], {layers: layerIds}
            )[0];

        if (!feature) {
            return;
        }

        const {name, code} = feature.properties;

        me.busStopPanel = new BusStopPanel({name, code});
        me.busStopPanel.addTo(me);
    }

    /**
     * 열려 있는 도착예정 패널을 닫는다.
     */
    hideBusStopArrivals() {
        const me = this;

        if (me.busStopPanel) {
            me.busStopPanel.remove();
            delete me.busStopPanel;
        }
    }

    pickObject(point) {
        const me = this,
            {map, layerZoom, viewMode} = me,
            modes = viewMode === 'underground' ? ['underground', 'ground'] : ['ground', 'underground'],
            pickVehicle = mode => {
                const object = me.trafficLayer.pickObject(mode, point);

                if (!object) {
                    return;
                }
                // A car behaves as its train, but remembers which car was clicked
                if (object.type === 'car') {
                    object.train.carNumber = object.carNumber;
                    object.train.carInstanceID = object.instanceID;
                    return object.train;
                }
                object.carNumber = 1;
                delete object.carInstanceID;
                return object;
            },
            pickStation = mode => {
                const feature = mode === 'ground' ?
                    map.queryRenderedFeatures(point, {layers: [`stations-og-${layerZoom}`]})[0] :
                    pickObject(map.__deck, `stations-ug-${layerZoom}`, point);

                return feature && me.stationGroupLookup.get(feature.properties.group);
            };
        let object;

        // 'both' 는 두 층이 한 화면에 겹친다. 역 원은 열차보다 크고 위에 그려져서
        // 지하 열차를 그냥 먹는다 — 승강장에 선 열차는 클릭도 hover 도 안 됐다.
        // 그래서 두 층의 열차를 먼저 다 보고, 없을 때 역을 본다.
        // 한쪽만 보는 모드는 순서를 그대로 둔다. 안 보이는 층의 열차가 집히면
        // 빈 곳을 눌렀는데 유령 열차가 잡히는 셈이다.
        if (viewMode === 'both') {
            for (const mode of modes) {
                object = pickVehicle(mode);
                if (object) {
                    return object;
                }
            }
            for (const mode of modes) {
                object = pickStation(mode);
                if (object) {
                    return object;
                }
            }
            return;
        }
        for (const mode of modes) {
            object = pickVehicle(mode);
            if (object) {
                return object;
            }
            object = pickStation(mode);
            if (object) {
                return object;
            }
        }
    }

    markObject(object) {
        const me = this,
            {markedObject, trafficLayer, map, popup} = me;

        if (isEqualObject(markedObject, object)) {
            return;
        }

        if (markedObject) {
            delete me.markedObject;
            if (isVehicle(markedObject)) {
                trafficLayer.markObject();
                if (markedObject.type === 'bus') {
                    me.updateBusTripHighlight(markedObject);
                }
            } else {
                me.removeStationOutline('stations-marked');
            }
            map.getCanvas().style.cursor = '';
            if (popup && popup.isOpen() && !(!object && isVehicle(me.trackedObject))) {
                popup.remove();
                delete me.popup;
            }
        }

        // 마우스가 떠나도 선택한 열차의 말풍선은 남긴다. 주체가 markedObject 에서
        // trackedObject 로 넘어가므로 내용과 위치를 그 열차 것으로 다시 쓴다.
        if (!object && isVehicle(me.trackedObject)) {
            if (!me.popup) {
                me.createPopup();
                me.updatePopup({setHTML: true, addToMap: true});
            } else {
                me.updatePopup({setHTML: true});
            }
        }

        if (object) {
            me.markedObject = object;
            if (isVehicle(object)) {
                me.updateObjectPosition(object);
                trafficLayer.markObject(object);
                if (object.type === 'bus') {
                    me.updateBusTripHighlight(object);
                }
            } else {
                me.addStationOutline(object, 'stations-marked');
            }

            map.getCanvas().style.cursor = 'pointer';
            // 선택으로 띄워둔 말풍선이 이미 있으면 그것을 쓴다. 새로 만들면 옛 DOM 이
            // 남아 화면에 두 개가 겹치고, 글자도 옛 것이 계속 보인다.
            if (!me.popup) {
                me.createPopup();
                me.updatePopup({setHTML: true, addToMap: true});
            } else {
                me.updatePopup({setHTML: true});
            }
        }
        me.updateAdTrainPopup();
        map.triggerRepaint();
    }

    trackObject(object) {
        const me = this,
            {searchMode, lang, map, trackedObject, trafficLayer, lastCameraParams, sharePanel, detailPanel} = me;

        if (searchMode === 'edit' && detailPanel && isStation(object)) {
            const {title, utitle} = object.stations[0],
                stationTitle = (utitle && utitle[lang]) || helpers.normalize(title[lang] || title.en);

            detailPanel.fillStationName(stationTitle);
            return;
        }

        if (isEqualObject(trackedObject, object)) {
            if ((isVehicle(object) || isStation(object)) && detailPanel) {
                detailPanel.reset();
            }
            // 같은 열차의 다른 칸을 클릭한 경우다. 아래 선택 처리는 건너뛰지만 윤곽선은
            // 옮겨야 한다 - 카메라는 매 프레임 carInstanceID 를 다시 읽어 따라가는데
            // 윤곽선은 여기서 한 번만 정해지므로 그냥 두면 선두 칸에 남는다.
            if (isVehicle(object)) {
                me.trackedCarInstanceID = object.carInstanceID;
                trafficLayer.trackObject(object);
                // 호차가 바뀌면 말풍선의 (N/10호차) 도 바뀐다
                me.updatePopup({setHTML: true});
            }
            return;
        }

        // 다른 대상으로 옮기면 이전 대상에 맞춰 잡아둔 카메라 각도는 버린다.
        delete me.cameraOverride;

        // Remember the camera params to restore them after the object is deselected
        if (!trackedObject && object) {
            lastCameraParams.viewMode = me.getViewMode();
            delete lastCameraParams.center;
            lastCameraParams.zoom = map.getZoom();
            lastCameraParams.bearing = map.getBearing();
            lastCameraParams.pitch = map.getPitch();
        } else if (trackedObject && !object) {
            me._setViewMode(lastCameraParams.viewMode);
            map.flyTo({
                center: lastCameraParams.center || map.getCenter(),
                zoom: lastCameraParams.zoom,
                bearing: lastCameraParams.bearing,
                pitch: lastCameraParams.pitch
            });
        }

        if (trackedObject) {
            delete me.trackedObject;
            delete me.trackedCarInstanceID;
            // 선택으로 띄워둔 말풍선은 선택이 풀리면 내린다. 마우스가 올라가 있으면
            // 그 대상의 말풍선이므로 남긴다.
            if (me.popup && !me.markedObject) {
                me.popup.remove();
                delete me.popup;
            }
            trafficLayer.hideCars();
            if (isVehicle(trackedObject)) {
                trafficLayer.trackObject();
                if (trackedObject.type === 'bus') {
                    me.updateBusTripHighlight(trackedObject);
                    me.updateBusRouteFocus();
                }
                me.fire({type: 'deselection', deselection: trackedObject.id});
            } else if (isStation(trackedObject)) {
                me.removeStationOutline('stations-selected');
                me.fire({type: 'deselection', deselection: trackedObject.stations.map(({id}) => id)});
                me._setSearchMode('none');
                me.hideStationExits();
            } else {
                me.fire(Object.assign({type: 'deselection'}, trackedObject));
            }
            me.updateHandlersAndControls();
            me.stopViewAnimation();
            // 선택 해제 경로에는 아래 if (object) 블록이 안 걸린다. 여기서 안 벗기면
            // 다음 클록 틱까지 열차가 혼잡도 색을 뒤집어쓴 채로 남는다.
            me.refreshCongestionColors();
            if (sharePanel) {
                sharePanel.remove();
                delete me.sharePanel;
            }
            if (detailPanel) {
                detailPanel.remove();
                delete me.detailPanel;
            }
        }

        if (object) {
            me.trackedObject = object;

            // Draw the tracked train in full, so its length is what it really is
            trafficLayer.showCars(object.type === 'train' ? object : undefined);
            me.refreshCongestionColors();

            if (isVehicle(object)) {
                me.updateObjectPosition(object);
                me.updateBaseZoom();
                me.updateTrackingParams(true);
                me.updateHandlersAndControls();
                me.startViewAnimation();
                // 'both' 는 이미 둘 다 보인다. 열차를 고를 때마다 한쪽만 보이는 모드로
                // 끌어내리면 사용자가 고른 모드를 앱이 되돌리는 셈이 된다.
                if (me.viewMode !== 'both') {
                    me._setViewMode(object.altitude < 0 ? 'underground' : 'ground');
                }

                if (helpers.includes(['train', 'flight'], object.type) && me.clockMode === 'realtime' && navigator.share) {
                    me.sharePanel = new SharePanel({object});
                    me.sharePanel.addTo(me);
                }
                // 시네마 모드에서는 상세 패널을 띄우지 않는다. 주기마다 열차가 바뀌어
                // 패널이 계속 새로 그려지고, 화면 아래 3분의 1을 덮어 시계·모드·전환주기
                // 컨트롤이 가려진다. 필요한 정보(노선·편성·혼잡도·다음 정차)는 말풍선에
                // 그대로 있다.
                if (!me.cinemaMode) {
                    if (object.timetable) {
                        me.detailPanel = new TrainPanel({object});
                        me.detailPanel.addTo(me);
                    } else if (object.trip) {
                        me.detailPanel = new BusPanel({object});
                        me.detailPanel.addTo(me);
                    }
                }

                me.trackedCarInstanceID = object.carInstanceID;
                trafficLayer.trackObject(object);
                // 선택하면 말풍선을 계속 띄운다. 마우스를 올려 이미 떠 있으면 그것을 쓴다.
                if (!me.popup) {
                    me.createPopup();
                    me.updatePopup({setHTML: true, addToMap: true});
                } else {
                    me.updatePopup({setHTML: true});
                }
                if (object.type === 'bus') {
                    me.updateBusTripHighlight(object);
                    me.updateBusRouteFocus();
                }
                me.fire({type: 'selection', selection: object.id});
            } else if (isStation(object)) {
                const stations = object.stations.concat(object.hidden || []),
                    coords = stations.map(station => station.coord),
                    center = lastCameraParams.center = helpersMapbox.getBounds(coords).getCenter();

                if (object.layer === 'ground') {
                    me._setViewMode('ground');
                } else if (!map.getCenter().distanceTo(center)) {
                    me._setViewMode('underground');
                } else {
                    let prevZoom = map.getZoom();
                    const initialZoom = prevZoom,
                        onZoom = () => {
                            const zoom = map.getZoom();

                            if (zoom < prevZoom && zoom < initialZoom - 0.5) {
                                me._setViewMode('ground');
                            } else if (zoom > prevZoom && zoom > 15) {
                                me._setViewMode('underground');
                            }
                            prevZoom = zoom;
                        };

                    map.on('zoom', onZoom);
                    map.once('zoomend', () => {
                        me._setViewMode('underground');
                        map.off('zoom', onZoom);
                    });
                }
                map.flyTo({center, zoom: 15.5});

                if (navigator.share) {
                    me.sharePanel = new SharePanel({object});
                    me.sharePanel.addTo(me);
                }
                me.detailPanel = new StationPanel({object: stations});
                me.detailPanel.addTo(me);

                me.addStationOutline(object, 'stations-selected');
                me.fire({type: 'selection', selection: object.stations.map(({id}) => id)});
            } else {
                me.fire(Object.assign({type: 'selection'}, object));
            }
        }
    }

    /**
     * Returns where the camera should sit for the tracked object.
     * 클릭으로 확정한 호차(trackedCarInstanceID)를 따라간다. object.carInstanceID 를
     * 직접 읽으면 안 된다 — pickObject 가 mousemove 에서도 그 값을 덮어쓰므로 마우스를
     * 올린 것만으로 카메라가 다른 칸으로 날아간다. 칸을 고르는 것은 클릭이다.
     * 열차의 논리 위치(object.coord)는 선두 칸 그대로 둔다 — 그 값은 역 근접 판정과
     * 광고열차 등 여러 곳이 읽으므로 카메라 사정으로 바꾸지 않는다.
     * @returns {Object} Object with coord, altitude and bearing
     */
    getTrackedCameraTarget() {
        const me = this,
            object = me.trackedObject,
            instanceID = me.trackedCarInstanceID;

        if (isVehicle(object) && instanceID !== undefined) {
            const {coord, altitude, bearing} = me.trafficLayer.getInstancePosition(instanceID);

            return {coord, altitude, bearing};
        }
        return object;
    }

    updateObjectPosition(object) {
        const {altitude: prevAltitude, standing: prevStanding} = object,
            {coord, altitude, bearing, _t} = this.trafficLayer.getObjectPosition(object);
        let viewMode, standing;

        object.coord = coord;
        object.altitude = altitude;
        object.bearing = bearing;
        object._t = _t;
        object.standing = _t === 0 || _t === 1;
        if (prevAltitude < 0 && altitude >= 0) {
            viewMode = 'ground';
        } else if (prevAltitude >= 0 && altitude < 0) {
            viewMode = 'underground';
        }
        if (prevStanding && !object.standing) {
            // Set only when starting
            standing = object.standing;
        }
        return {viewMode, standing};
    }

    showAdTrainPopup(train) {
        const me = this,
            adTrains = me.adTrains;

        if (adTrains.has(train)) {
            return;
        }

        const ad = train.ad;

        train.popup = new AnimatedPopup({
            className: 'popup-ad-cars',
            closeButton: false,
            closeOnClick: false,
            offset: {
                top: [0, 10],
                bottom: [0, -30]
            }
        });
        train.popup.setHTML(`<span style="color: ${ad.textcolor};">${ad.title[me.lang]}</span>`);
        adTrains.add(train);
        me.updateObjectPosition(train);
        me.updateAdTrainPopup();
    }

    updateAdTrainPopup() {
        const me = this,
            markedObject = me.markedObject;

        for (const train of me.adTrains) {
            train.popup.setLngLat(me.adjustCoord(train.coord, train.altitude));
            if (train !== markedObject && !train.popupVisible) {
                train.popup.addTo(me.map);
                train.popupVisible = true;
            } else if (train === markedObject && train.popupVisible) {
                train.popup.remove();
                delete train.popupVisible;
            }
        }
    }

    hideAdTrainPopup(train) {
        const me = this,
            adTrains = me.adTrains;

        if (!adTrains.has(train) || !train.popup) {
            return;
        }
        train.popup.remove();
        delete train.popup;
        delete train.popupVisible;
        adTrains.delete(train);
    }

    showStationExits(stations) {
        const me = this,
            map = me.map,
            exits = [].concat(...stations.map(station => station.exit || []));

        if (exits.length > 0) {
            const coords = [];

            me.exitPopups = exits.map((poi, index) => {
                const {coord, facilities = []} = poi,
                    icons = facilities.map(facility => `<span class="exit-${facility}-small-icon"></span>`).join(''),
                    listener = () => {
                        me.exitPopups[index] = setTimeout(() => {
                            const popup = new AnimatedPopup({
                                className: 'popup-exit',
                                closeButton: false,
                                closeOnClick: false
                            });

                            popup.setLngLat(coord)
                                .setHTML(icons + me.getLocalizedPOITitle(poi))
                                .addTo(map)
                                .getElement().id = `exit-${index}`;

                            me.exitPopups[index] = popup;
                        }, index / exits.length * 1000);
                    };

                map.once('moveend', listener);
                coords.push(coord);

                return listener;
            });

            map.fitBounds(helpersMapbox.getBounds(coords), {
                bearing: map.getBearing(),
                pitch: map.getPitch(),
                offset: [0, -map.transform.height / 12],
                padding: {top: 20, bottom: 20, left: 10, right: 60},
                retainPadding: false,
                maxZoom: 18
            });
        }
    }

    hideStationExits() {
        const me = this;

        for (const popup of me.exitPopups) {
            if (popup instanceof AnimatedPopup) {
                popup.remove();
            } else if (typeof popup === 'function') {
                me.map.off('moveend', popup);
            } else {
                clearTimeout(popup);
            }
        }
        me.exitPopups = [];
    }

    updateBaseZoom() {
        const me = this,
            {map, trackedObject: object} = me;

        if (isVehicle(object)) {
            const objectZ = me.getModelPosition(object.coord, object.altitude).z,
                cameraZ = getCameraMercatorZ(map);

            me.baseZoom = map.getZoom() + Math.log2(cameraZ / Math.abs(cameraZ - objectZ));
        }
    }

    /**
     * Records a manual camera angle so tracking stops overwriting it every frame.
     * Heading-derived modes keep the angle relative to the train's direction, so the
     * chase camera survives a curve; the orbiting modes give up their animation and
     * hold the absolute angle the user chose.
     */
    updateCameraOverride() {
        const me = this,
            {map, trackedObject, trackingMode, cameraOverride} = me,
            bearing = map.getBearing();

        if (!isVehicle(trackedObject) || trackingMode === 'position') {
            return;
        }
        if (HEADING_TRACKING_MODES.has(trackingMode)) {
            // 프레임 사이에 rotate 가 두 번 이상 오면 lastAppliedBearing 이 낡아 같은
            // 회전을 두 번 세므로, 델타를 먹은 자리에서 기준점을 바로 당겨둔다.
            const delta = wrapAngle(bearing - me.lastAppliedBearing),
                offset = ((cameraOverride && cameraOverride.bearingOffset) || 0) + delta;

            me.cameraOverride = {
                // 누적 오프셋을 (-180, 180] 로 정규화한다. 안 하면 계속 돌릴수록
                // 값이 무한히 커지고, 190도는 -170도와 같은 각인데 다르게 취급된다.
                bearingOffset: wrapAngle(offset),
                pitch: map.getPitch()
            };
            me.lastAppliedBearing = bearing;
        } else {
            me.cameraOverride = {bearing, pitch: map.getPitch()};
        }
    }

    /**
     * Creates the object popup. 마우스를 올렸을 때와 열차를 선택했을 때 둘 다 쓴다.
     */
    createPopup() {
        this.popup = new AnimatedPopup({
            className: helpers.isTouchDevice() ? 'popup-object popup-touch' : 'popup-object',
            closeButton: false,
            closeOnClick: false,
            maxWidth: '300px',
            offset: {
                top: [0, 10],
                bottom: [0, -30]
            },
            openingAnimation: {
                duration: 300,
                easing: 'easeOutBack'
            }
        });
    }

    /**
     * Returns what the popup is describing: 마우스를 올린 대상이 우선이고, 없으면
     * 선택한 열차다. 그래서 마우스가 지도를 떠나도 선택 중인 열차 정보는 남는다.
     * @returns {Object} Marked or tracked object
     */
    getPopupObject() {
        const me = this;

        return me.markedObject || (isVehicle(me.trackedObject) ? me.trackedObject : undefined);
    }

    updatePopup(options) {
        const me = this,
            {map, popup} = me,
            markedObject = me.getPopupObject(),
            {setHTML, addToMap} = options || {};

        if (!popup || !markedObject) {
            return;
        }

        if (isVehicle(markedObject)) {
            const bearing = markedObject === me.trackedObject ? map.getBearing() : undefined,
                {coord, altitude} = markedObject.carInstanceID !== undefined ?
                    me.trafficLayer.getPickedPosition(markedObject) : markedObject;

            popup.setLngLat(me.adjustCoord(coord, altitude, bearing));
            if (setHTML) {
                popup.setHTML(
                    markedObject.type === 'train' ? me.getTrainDescription(markedObject) :
                    markedObject.type === 'flight' ? me.getFlightDescription(markedObject) :
                    me.getBusDescription(markedObject)
                );
            }
        } else {
            const object = me.featureLookup.get(`${markedObject.id}.${me.layerZoom}`),
                coord = helpersGeojson.getCenterCoord(object),
                altitude = helpersGeojson.getAltitude(object);

            popup.setLngLat(me.adjustCoord(coord, altitude));
            if (setHTML) {
                const stations = markedObject.stations,
                    railwayColors = {};

                for (const station of stations) {
                    const title = me.getLocalizedStationTitle(station),
                        railway = station.railway,
                        colors = railwayColors[title] = railwayColors[title] || {};

                    colors[me.getLocalizedRailwayTitle(railway)] = railway.color;
                }
                popup.setHTML([
                    '<div class="railway-list">',
                    Object.keys(railwayColors).map(stationTitle => {
                        const railwayTitles = Object.keys(railwayColors[stationTitle])
                            .map(railwayTitle => `<div class="line-strip" style="background-color: ${railwayColors[stationTitle][railwayTitle]};"></div><span>${railwayTitle}</span>`)
                            .join('<br>');

                        return `<strong>${stationTitle}</strong><br>${railwayTitles}`;
                    }).join('<br>'),
                    '</div>'
                ].join(''));
            }
        }
        if (addToMap) {
            popup.addTo(map);
        }
    }

    /**
     * 노선선의 표시 여부를 다시 적용한다. 두 가지가 겹친다:
     * 노선 필터(route.hidden)와 선택 집중(busFocus). 버스 메시는 별도 레이어라
     * 여기서 손대지 않는다 - 선만 지우고 차량은 남긴다.
     */
    _applyBusRouteVisibility() {
        const me = this,
            {map, busFocus} = me;

        for (const gtfs of me.gtfs.values()) {
            const {id: source, routes} = gtfs,
                shapes = new Set();

            for (const route of routes.getAll()) {
                if (!route.hidden) {
                    for (const shape of route.shapes) {
                        shapes.add(shape);
                    }
                }
            }
            for (const id of gtfs.featureLookup.keys()) {
                const visible = shapes.has(id) && (!busFocus ||
                    (busFocus.gtfsId === gtfs.id && busFocus.shapes.has(id)));

                // ⚠️ **removeFeatureState 를 쓰면 안 된다.** MapLibre 의
                // coalesceChanges 는 지울 상태를 `delete this.state[layer][id][key]`
                // 로 지우는데, 그 feature 에 상태가 **한 번도 설정된 적 없으면**
                // `this.state[layer][id]` 가 undefined 라 TypeError 로 던진다.
                // 그리고 deletedStates 를 비우는 줄이 그 뒤에 있어서 **매 프레임
                // 영원히 다시 던진다** - 지도가 통째로 멈춘다 (실측 2026-09-09,
                // 버스를 클릭하면 화면이 정지. 스택: coalesceChanges <- prepare
                // <- render). 세 상태 전부 `to-boolean`/`string` 으로 읽으므로
                // false 는 '없음' 과 같게 동작한다.
                map.setFeatureState({source, id}, {hidden: !visible});
            }
        }
    }

    /**
     * 버스를 선택하면 그 노선의 선만 남기고 나머지 노선선을 감춘다.
     * 선택이 풀리면 원래대로 돌린다. 다른 노선의 **버스는 그대로 남는다**.
     */
    updateBusRouteFocus() {
        const me = this,
            tracked = me.trackedObject;

        if (tracked && tracked.type === 'bus') {
            const gtfs = me.gtfs.get(tracked.gtfsId),
                route = gtfs && gtfs.routes.get(tracked.trip.route);

            me.busFocus = route ?
                {gtfsId: tracked.gtfsId, shapes: new Set(route.shapes)} : undefined;
        } else {
            delete me.busFocus;
        }
        me._applyBusRouteVisibility();
    }

    updateBusRouteVisibility() {
        const me = this;

        me._applyBusRouteVisibility();
        for (const gtfs of me.gtfs.values()) {
            const {routes} = gtfs;

            for (const bus of gtfs.activeBusLookup.values()) {
                const route = routes.get(bus.trip.route);

                if (!route || route.hidden) {
                    me.stopBus(bus);
                }
            }
        }
        delete me.lastRefresh;
        delete me.lastRealtimeCheck;
    }

    updateBusRouteHighlight(gtfsId, routeId) {
        const me = this,
            map = me.map;

        if (gtfsId) {
            const gtfs = me.gtfs.get(gtfsId),
                route = gtfs.routes.get(routeId);

            for (const id of route.shapes) {
                map.setFeatureState({source: gtfsId, id}, {'route-highlight': route.color || gtfs.color});
            }
        } else {
            for (const gtfs of me.gtfs.values()) {
                for (const id of gtfs.featureLookup.keys()) {
                    // 위 _applyBusRouteVisibility 주석 참조 - remove 는 던진다
                    map.setFeatureState({source: gtfs.id, id}, {'route-highlight': false});
                }
            }
        }
    }

    updateBusTripHighlight(object) {
        const me = this,
            {map, markedObject, trackedObject} = me,
            {gtfsId, trip} = object,
            source = gtfsId,
            id = trip.shape;

        if ((markedObject && markedObject.type === 'bus' && markedObject.gtfsId === gtfsId && markedObject.trip.shape === id) ||
            (trackedObject && trackedObject.type === 'bus' && trackedObject.gtfsId === gtfsId && trackedObject.trip.shape === id)) {
            const gtfs = me.gtfs.get(gtfsId),
                color = gtfs.routes.get(trip.route).color;

            map.setFeatureState({source, id}, {'trip-highlight': color || gtfs.color});
        } else {
            // 위 _applyBusRouteVisibility 주석 참조 - remove 는 던진다
            map.setFeatureState({source, id}, {'trip-highlight': false});
        }
    }

    addStationOutline(object, name) {
        const me = this,
            id = object.stations[0].id;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(me.map, `${name}-${zoom}`, {
                data: helpersGeojson.featureFilter(me.featureCollection, p => p.zoom === zoom && p.ids && p.ids[0] === id),
                visible: true
            });
        }
    }

    removeStationOutline(name) {
        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `${name}-${zoom}`, {
                visible: false
            });
        }
    }

    refreshStationOutline() {
        const opacity = helpers.blink();

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `stations-selected-${zoom}`, {
                opacity,
                visible: true
            });
        }
    }

    setSectionData(train, index, final) {
        const stations = train.r.stations,
            {direction, timetable} = train,
            destination = (train.ds || [])[0],
            delay = train.delay || 0,
            now = this.clock.getTimeOffset();
        let arrivalTimes, departureTimes, ttIndex, departureStation, arrivalStation, currentSection, nextSection, finalSection;

        if (timetable) {
            const s = timetable.stations;

            arrivalTimes = timetable.arrivalTimes;
            departureTimes = timetable.departureTimes;
            ttIndex = helpers.valueOrDefault(index, departureTimes.reduce(
                (acc, cur, i) => cur !== undefined && cur + delay <= now ? i : acc, 0
            ));
            departureStation = s[ttIndex];
            arrivalStation = s[ttIndex + 1];
        } else {
            departureStation = train.fs || train.ts;
            arrivalStation = train.ts || train.fs;
        }

        if (direction > 0) {
            currentSection = stations.indexOf(departureStation);
            nextSection = stations.indexOf(arrivalStation, currentSection);
            finalSection = stations.indexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = stations.length - 1;
            }
        } else {
            currentSection = stations.lastIndexOf(departureStation);
            nextSection = stations.lastIndexOf(arrivalStation, currentSection);
            finalSection = stations.lastIndexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = 0;
            }
        }

        if (timetable) {
            train.timetableIndex = ttIndex;
            train.departureStation = departureStation;
            train.departureTime = helpers.valueOrDefault(departureTimes[ttIndex], arrivalTimes[ttIndex]);

            if (currentSection >= 0 && nextSection >= 0) {
                train.sectionIndex = currentSection;
                train.sectionLength = nextSection - currentSection;
                train.arrivalStation = arrivalStation;
                train.arrivalTime = arrivalTimes[ttIndex + 1];
                train.nextDepartureTime = departureTimes[ttIndex + 1];

                return true;
            }

        } else {
            let actualSection;

            if (train.sectionIndex === undefined) {
                actualSection = nextSection = currentSection;
            } else {
                actualSection = train.sectionIndex + train.sectionLength;
            }

            train.departureStation = departureStation;

            if (actualSection >= 0 && actualSection !== finalSection && ((!final && nextSection >= 0) || (final && finalSection >= 0))) {
                train.sectionIndex = actualSection;
                train.sectionLength = (final ? finalSection : nextSection) - actualSection;
                train.arrivalStation = arrivalStation === departureStation ? stations[actualSection + direction] : arrivalStation;

                return true;
            }
        }

        train.arrivalStation = train.arrivalTime = undefined;
    }

    /**
     * 시간표 버스의 다음 구간을 잡는다. **실시간 버스는 `busTrack` 이 굴린다.**
     * @param {Object} bus - 버스
     * @param {number} [index] - 갈 구간. 없으면 지금 시각으로 고른다
     * @returns {boolean} 더 갈 구간이 있나
     */
    setBusSectionData(bus, index) {
        const {stopSequences, departureTimes} = bus.trip,
            now = this.clock.getTimeOffset(),
            currentSection = helpers.valueOrDefault(index, departureTimes.reduce(
                (acc, cur, i) => cur <= now ? i : acc, 0
            ));

        if (currentSection !== stopSequences.length - 1) {
            bus.sectionIndex = currentSection;
            bus.sectionLength = 1;
            bus.departureTime = departureTimes[currentSection];
            bus.nextDepartureTime = departureTimes[currentSection + 1];

            return true;
        }
    }
}

// Normalizes a data source, appending consumer keys to its URLs based on the
// host (see updateOdptUrl). What a source loads is determined by which URLs it
// carries: trainUrl/trainInfoUrl (trains), flightUrl/atisUrl (flights) and
// gtfsUrl/vehiclePositionUrl/color/frequencyBased (GTFS bus).
function normalizeDataSource(source, secrets) {
    return {
        id: source.id,
        trainUrl: updateOdptUrl(source.trainUrl, secrets),
        trainInfoUrl: updateOdptUrl(source.trainInfoUrl, secrets),
        flightUrl: updateOdptUrl(source.flightUrl, secrets),
        atisUrl: updateOdptUrl(source.atisUrl, secrets),
        gtfsUrl: updateOdptUrl(source.gtfsUrl, secrets),
        vehiclePositionUrl: updateOdptUrl(source.vehiclePositionUrl, secrets),
        // 재생 모드에서 갈아끼울 **관측 시간표** 피드. `{date}`(YYYYMMDD)가 치환된다.
        // 같은 디렉토리의 index.json 이 가진 날짜 목록만 쓴다.
        historyUrl: source.historyUrl,
        color: source.color,
        // 배차간격 기반 피드(공표 다이어 없음). 상세 패널이 시각 표시를 뺀다.
        frequencyBased: !!source.frequencyBased,
        expiresAt: source.expiresAt
    };
}

function initContainer(container) {
    const map = helpers.createElement('div', {
        id: 'map'
    }, container);

    helpers.createElement('div', {
        id: 'loader',
        className: 'loader-inner ball-pulse',
        innerHTML: '<div></div><div></div><div></div>'
    }, container);
    helpers.createElement('div', {
        id: 'loading-error'
    }, container);
    container.classList.add('mini-seoul-3d');
    return map;
}

function showLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 1;
    element.style.display = 'block';
}

function hideLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 0;
    setTimeout(() => {
        element.style.display = 'none';
    }, 1000);
}

function showErrorMessage(container) {
    const loaderElement = container.querySelector('#loader'),
        errorElement = container.querySelector('#loading-error');

    loaderElement.style.display = 'none';
    errorElement.innerHTML = 'Loading failed. Please reload the page.';
    errorElement.style.display = 'block';
}

function easeInQuad(t) {
    return t * t;
}

function easeOutQuart(t) {
    return -((t -= 1) * t * t * t - 1);
}

function createInterpolant(xs, ys) {
    const length = xs.length;

    // Get consecutive differences and slopes
    const dys = [], dxs = [], ms = [];
    for (let i = 0; i < length - 1; i++) {
        const dx = xs[i + 1] - xs[i], dy = ys[i + 1] - ys[i];
        dxs.push(dx); dys.push(dy); ms.push(dy / dx);
    }

    // Get degree-1 coefficients
    const c1s = [ms[0]];
    for (let i = 0; i < dxs.length - 1; i++) {
        const m = ms[i], mNext = ms[i + 1];
        if (m * mNext <= 0) {
            c1s.push(0);
        } else {
            const dx_ = dxs[i], dxNext = dxs[i + 1], common = dx_ + dxNext;
            c1s.push(3 * common / ((common + dxNext) / m + (common + dx_) / mNext));
        }
    }
    c1s.push(ms[ms.length - 1]);

    // Get degree-2 and degree-3 coefficients
    const c2s = [], c3s = [];
    for (let i = 0; i < c1s.length - 1; i++) {
        const c1 = c1s[i], m_ = ms[i], invDx = 1 / dxs[i], common_ = c1 + c1s[i + 1] - m_ - m_;
        c2s.push((m_ - c1 - common_) * invDx); c3s.push(common_ * invDx * invDx);
    }

    // Return interpolant function
    return x => {
        // The rightmost point in the dataset should give an exact result
        let i = xs.length - 1;
        if (x === xs[i]) {
            return ys[i];
        }

        // Search for the interval x is in, returning the corresponding y if x is one of the original xs
        let low = 0, mid, high = c3s.length - 1;
        while (low <= high) {
            mid = Math.floor(0.5 * (low + high));
            const xHere = xs[mid];
            if (xHere < x) {
                low = mid + 1;
            } else if (xHere > x) {
                high = mid - 1;
            } else {
                return ys[mid];
            }
        }
        i = Math.max(0, high);

        // Interpolate
        const diff = x - xs[i], diffSq = diff * diff;
        return ys[i] + c1s[i] * diff + c2s[i] * diffSq + c3s[i] * diff * diffSq;
    };
}

function getLayerZoom(zoom) {
    return helpers.clamp(Math.floor(zoom), 13, 18);
}

// Deprecated
// function getObjectScale(zoom) {
//     return Math.pow(2, 14 - helpers.clamp(zoom, 13, 19));
// }

function isVehicle(object) {
    return object && helpers.includes(['train', 'flight', 'bus'], object.type);
}

function isStation(object) {
    return object && object.type === 'station';
}

function isEqualObject(a, b) {
    if (a === b) {
        return true;
    }
    if (a && a.properties && b && b.properties && a.properties.ids === b.properties.ids) {
        return true;
    }
    return false;
}
